export interface Atom {
    number: number;
    color: string;
    name: string;
    title: {
        en: string;
        de: string;
        fr: string;
    };
    letter: string;
    conventionalWeight?: number;
    weight: string;
    firstIonization?: number;
    electronegativity?: string;
    noStableIsotope?: boolean;
    info: AtomInfo;
}

export interface AtomInfo {
    image: string;
    link: string;
    imageText: string;
    description: string;
    isotopes: Array<AtomIsotope>;
}

export interface AtomIsotope {
    number: number;
    radioactive: boolean;
    weight: number;
    percent: number;
    moleFraction?: Array<{ fractionLower: number; fractionUpper: number }>;
}

export const ATOMS: Array<Atom> = [
    {
        number: 1,
        color: "pink",
        name: "hydrogen",
        title: {
            en: "Hydrogen",
            de: "Wasserstoff",
            fr: "Hydrogène",
        },
        letter: "H",
        conventionalWeight: 1.008,
        weight: "[1.00784, 1.00811]",
        firstIonization: 13.59,
        electronegativity: "2.1",
        info: {
            image: "images/hydrogen.jpg",
            link: "pdf-elements/hydrogen.pdf",
            imageText:
                "Variation in fraction of <sup>2</sup>H of discharge weighted mean values of selected rivers of the United States. Blue color indicates waters most depleted in <sup>2</sup>H and brown indicates those most enriched in <sup>2</sup>H.",
            description: "",
            isotopes: [
                {
                    number: 1,
                    radioactive: false,
                    weight: 1.007825032,
                    percent: 99.9885,
                    moleFraction: [
                        {
                            fractionLower: 0.99972,
                            fractionUpper: 0.99999,
                        },
                    ],
                },
                {
                    number: 2,
                    radioactive: false,
                    weight: 2.0141017781,
                    percent: 0.0115,
                    moleFraction: [
                        {
                            fractionLower: 0.00001,
                            fractionUpper: 0.00028,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 2,
        color: "yellow",
        name: "helium",
        title: {
            en: "Helium",
            de: "Helium",
            fr: "Hélium",
        },
        letter: "He",
        weight: "4.002602(2)",
        firstIonization: 24.58,
        info: {
            image: "images/helium.jpg",
            link: "pdf-elements/helium.pdf",
            imageText:
                "Radiation detectors are installed in many areas to screen people, vehicles, and cargo for radioactive materials. <sup>3</sup>He detectors are sensitive to thermal neutrons and can be used to detect isotopes of uranium and plutonium that might be used in nuclear weapons along with other sources that produce neutrons by radioactive decay.",
            description: "",
            isotopes: [
                {
                    number: 4,
                    radioactive: false,
                    weight: 4.0026032541,
                    percent: 99.9998,
                },
                {
                    number: 3,
                    radioactive: false,
                    weight: 3.01602932,
                    percent: 0.0002,
                },
            ],
        },
    },
    {
        number: 3,
        color: "pink",
        name: "lithium",
        title: {
            en: "Lithium",
            de: "Lithium",
            fr: "Lithium",
        },
        letter: "Li",
        conventionalWeight: 6.94,
        weight: "[6.938, 6.997]",
        firstIonization: 5.391,
        electronegativity: "1.0",
        info: {
            image: "images/lithium.jpg",
            link: "pdf-elements/lithium.pdf",
            imageText:
                "The relative abundances of lithium isotopes can change during hydrothermal processes, isotopic analysis of lithium in water can help distinguish water derived from marine sedimentary rocks from water derived from hydrothermally altered igneous rocks.",
            description: "",
            isotopes: [
                {
                    number: 7,
                    radioactive: false,
                    weight: 7.01600344,
                    percent: 92.41,
                    moleFraction: [
                        {
                            fractionLower: 0.922,
                            fractionUpper: 0.981,
                        },
                    ],
                },
                {
                    number: 6,
                    radioactive: false,
                    weight: 6.015122887,
                    percent: 7.59,
                    moleFraction: [
                        {
                            fractionLower: 0.019,
                            fractionUpper: 0.078,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 4,
        color: "blue",
        name: "beryllium",
        title: {
            en: "Beryllium",
            de: "Beryllium",
            fr: "Béryllium",
        },
        letter: "Be",
        weight: "9.0121831(5)",
        firstIonization: 9.322,
        electronegativity: "1.5",
        info: {
            image: "images/beryllium.jpg",
            link: "pdf-elements/beryllium.pdf",
            imageText:
                "Variability in <sup>10</sup>Be production as a result of the interaction of cosmic rays with exposed rocks at three sites on the Level 2 terrace in upper Holtwood Gorge, Pennsylvania, approximately 50 km upstream of Chesapeake Bay [38].",
            description: "",
            isotopes: [
                {
                    number: 9,
                    radioactive: false,
                    weight: 9.0121831,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 5,
        color: "pink",
        name: "boron",
        title: {
            en: "Boron",
            de: "Bor",
            fr: "Bore",
        },
        letter: "B",
        conventionalWeight: 10.81,
        weight: "[10.806, 10.821]",
        firstIonization: 8.298,
        electronegativity: "2.0",
        info: {
            image: "images/boron.jpg",
            link: "pdf-elements/boron.pdf",
            imageText:
                "Diagram of a typical pressurized water reactor. Boron is used in the control rods of nuclear reactors such as these to absorb neutrons and control reactor operation.",
            description: "",
            isotopes: [
                {
                    number: 11,
                    radioactive: false,
                    weight: 11.009305,
                    percent: 80.1,
                    moleFraction: [
                        {
                            fractionLower: 0.189,
                            fractionUpper: 0.2014,
                        },
                    ],
                },
                {
                    number: 10,
                    radioactive: false,
                    weight: 10.012937,
                    percent: 19.9,
                    moleFraction: [
                        {
                            fractionLower: 0.796,
                            fractionUpper: 0.811,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 6,
        color: "pink",
        name: "carbon",
        title: {
            en: "Carbon",
            de: "Kohlenstoff",
            fr: "Carbone",
        },
        letter: "C",
        conventionalWeight: 12.011,
        weight: "[12.0096, 12.0116]",
        firstIonization: 11.26,
        electronegativity: "2.5",
        info: {
            image: "images/carbon.jpg",
            link: "pdf-elements/carbon.pdf",
            imageText:
                "Variations in the <i>n</i>(<sup>13</sup>C)/<i>n</i>(<sup>12</sup>C) ratio of marijuana can be analyzed by EA-IRMS to determine if the plants were grown “inside” a building or greenhouse or were “open grown.”",
            description: "",
            isotopes: [
                {
                    number: 12,
                    radioactive: false,
                    weight: 12,
                    percent: 98.93,
                    moleFraction: [
                        {
                            fractionLower: 0.9884,
                            fractionUpper: 0.9904,
                        },
                    ],
                },
                {
                    number: 13,
                    radioactive: false,
                    weight: 13.003354835,
                    percent: 1.07,
                    moleFraction: [
                        {
                            fractionLower: 0.0096,
                            fractionUpper: 0.0116,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 7,
        color: "pink",
        name: "nitrogen",
        title: {
            en: "Nitrogen",
            de: "Stickstoff",
            fr: "Azote",
        },
        letter: "N",
        conventionalWeight: 14.007,
        weight: "[14.00643, 14.00728]",
        firstIonization: 14.53,
        electronegativity: "3.0",
        info: {
            image: "images/nitrogen.jpg",
            link: "pdf-elements/nitrogen.pdf",
            imageText:
                "Nitrogen stable isotopes can be used to track fertilizer nitrogen into plants, soils, and infiltrating groundwater in experiments to improve agricultural efficiency and reduce impacts on the environment.",
            description: "",
            isotopes: [
                {
                    number: 14,
                    radioactive: false,
                    weight: 14.003074004,
                    percent: 99.636,
                    moleFraction: [
                        {
                            fractionLower: 0.99578,
                            fractionUpper: 0.99663,
                        },
                    ],
                },
                {
                    number: 15,
                    radioactive: false,
                    weight: 15.000108899,
                    percent: 0.364,
                    moleFraction: [
                        {
                            fractionLower: 0.00337,
                            fractionUpper: 0.00422,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 8,
        color: "pink",
        name: "oxygen",
        title: {
            en: "Oxygen",
            de: "Sauerstoff",
            fr: "Oxygène",
        },
        letter: "O",
        conventionalWeight: 15.999,
        weight: "[15.99903, 15.99977]",
        firstIonization: 13.61,
        electronegativity: "3.5",
        info: {
            image: "images/oxygen.jpg",
            link: "pdf-elements/oxygen.pdf",
            imageText:
                "Variation in fraction of <sup>18</sup>O of discharge weighted mean values of selected rivers of the United States. Blue color indicates waters most depleted in <sup>18</sup>O and brown indicates those most enriched in <sup>18</sup>O.",
            description: "",
            isotopes: [
                {
                    number: 16,
                    radioactive: false,
                    weight: 15.99491462,
                    percent: 99.757,
                    moleFraction: [
                        {
                            fractionLower: 0.99738,
                            fractionUpper: 0.99776,
                        },
                    ],
                },
                {
                    number: 17,
                    radioactive: false,
                    weight: 16.999131757,
                    percent: 0.038,
                    moleFraction: [
                        {
                            fractionLower: 0.000367,
                            fractionUpper: 0.0004,
                        },
                    ],
                },
                {
                    number: 18,
                    radioactive: false,
                    weight: 17.999159613,
                    percent: 0.205,
                    moleFraction: [
                        {
                            fractionLower: 0.00187,
                            fractionUpper: 0.00222,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 9,
        color: "blue",
        name: "fluorine",
        title: {
            en: "Fluorine",
            de: "Fluor",
            fr: "Fluor",
        },
        letter: "F",
        weight: "18.998403163(6)",
        firstIonization: 17.42,
        electronegativity: "4.0",
        info: {
            image: "images/fluorine.jpg",
            link: "pdf-elements/fluorine.pdf",
            imageText:
                "An <sup>18</sup>F-FDG PET scan is used to observe the differences in brain activity between a sober and an intoxicated brain.",
            description: "",
            isotopes: [
                {
                    number: 19,
                    radioactive: false,
                    weight: 18.998403163,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 10,
        color: "yellow",
        name: "neon",
        title: {
            en: "Neon",
            de: "Neon",
            fr: "Néon",
        },
        letter: "Ne",
        weight: "20.1797(6)",
        firstIonization: 21.56,
        info: {
            image: "images/neon.jpg",
            link: "pdf-elements/neon.pdf",
            imageText:
                "Glacial erratic boulder in Fordham Gneiss in the New York Botanical Gardens. Cosmogenic <sup>21</sup>Ne dating can be used to determine time since such blocks were covered by ice.",
            description: "",
            isotopes: [
                {
                    number: 20,
                    radioactive: false,
                    weight: 19.99244018,
                    percent: 90.48,
                },
                {
                    number: 22,
                    radioactive: false,
                    weight: 21.9913851,
                    percent: 9.25,
                },
                {
                    number: 21,
                    radioactive: false,
                    weight: 20.9938467,
                    percent: 0.27,
                },
            ],
        },
    },
    {
        number: 11,
        color: "blue",
        name: "sodium",
        title: {
            en: "Sodium",
            de: "Natrium",
            fr: "Sodium",
        },
        letter: "Na",
        weight: "22.98976928(2)",
        firstIonization: 5.139,
        electronegativity: "0.9",
        info: {
            image: "images/sodium.jpg",
            link: "pdf-elements/sodium.pdf",
            imageText:
                "<sup>22</sup>Na could be used to date the groundwater being discharged from springs.",
            description: "",
            isotopes: [
                {
                    number: 23,
                    radioactive: false,
                    weight: 22.98976928,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 12,
        color: "pink",
        name: "magnesium",
        title: {
            en: "Magnesium",
            de: "Magnesium",
            fr: "Magnésium",
        },
        letter: "Mg",
        conventionalWeight: 24.305,
        weight: "[24.304, 24.307]",
        firstIonization: 7.646,
        electronegativity: "1.2",
        info: {
            image: "images/magnesium.jpg",
            link: "pdf-elements/magnesium.pdf",
            imageText:
                "The anomalous abundance of <sup>26</sup>Mg in meteorite inclusions indicate that this material must have been formed early in the development of the solar system.",
            description: "",
            isotopes: [
                {
                    number: 24,
                    radioactive: false,
                    weight: 23.9850417,
                    percent: 78.99,
                    moleFraction: [
                        {
                            fractionLower: 0.7888,
                            fractionUpper: 0.7905,
                        },
                    ],
                },
                {
                    number: 26,
                    radioactive: false,
                    weight: 25.982593,
                    percent: 11.01,
                    moleFraction: [
                        {
                            fractionLower: 0.1096,
                            fractionUpper: 0.1109,
                        },
                    ],
                },
                {
                    number: 25,
                    radioactive: false,
                    weight: 24.985837,
                    percent: 10.0,
                    moleFraction: [
                        {
                            fractionLower: 0.09988,
                            fractionUpper: 0.10034,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 13,
        color: "blue",
        name: "aluminium",
        title: {
            en: "Aluminium",
            de: "Aluminium",
            fr: "Aluminium",
        },
        letter: "Al",
        weight: "26.9815385(7)",
        firstIonization: 5.985,
        electronegativity: "1.5",
        info: {
            image: "images/aluminium.jpg",
            link: "pdf-elements/aluminium.pdf",
            imageText:
                "Soil erosion off the coast of Kodiak, Alaska. The ratio of <sup>26</sup>Al to <sup>10</sup>Be can be used to study erosion and transport of soil and sediments on a 100,000- to a million year time scale. ",
            description: "",
            isotopes: [
                {
                    number: 27,
                    radioactive: false,
                    weight: 26.9815385,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 14,
        color: "pink",
        name: "silicon",
        title: {
            en: "Silicon",
            de: "Silicium",
            fr: "Silicium",
        },
        letter: "Si",
        conventionalWeight: 28.085,
        weight: "[28.084, 28.086]",
        firstIonization: 8.151,
        electronegativity: "1.8",
        info: {
            image: "images/silicon.jpg",
            link: "pdf-elements/silicon.pdf",
            imageText:
                "Cosmogenic <sup>32</sup>Si is used to date glaciers, like the Hubbard Glacier in Alaska.",
            description: "",
            isotopes: [
                {
                    number: 28,
                    radioactive: false,
                    weight: 27.976926535,
                    percent: 92.223,
                    moleFraction: [
                        {
                            fractionLower: 0.92191,
                            fractionUpper: 0.92318,
                        },
                    ],
                },
                {
                    number: 29,
                    radioactive: false,
                    weight: 28.976494665,
                    percent: 4.685,
                    moleFraction: [
                        {
                            fractionLower: 0.04645,
                            fractionUpper: 0.04699,
                        },
                    ],
                },
                {
                    number: 30,
                    radioactive: false,
                    weight: 29.9737701,
                    percent: 3.092,
                    moleFraction: [
                        {
                            fractionLower: 0.03037,
                            fractionUpper: 0.0311,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 15,
        color: "blue",
        name: "phosphorus",
        title: {
            en: "Phosphorus",
            de: "Phosphor",
            fr: "Phosphore",
        },
        letter: "P",
        weight: "30.973761998(5)",
        firstIonization: 10.48,
        electronegativity: "2.1",
        info: {
            image: "images/phosphorus.jpg",
            link: "pdf-elements/phosphorus.pdf",
            imageText:
                "Partitioning of <sup>32</sup>P among water layers, the sediments, and outflow during the 105 days following addition of <sup>32</sup>P to the upper layer of stratified Lake 227 (northwestern Ontario) to trace the lake's phosphorus cycle during late stratification and fall overturn.",
            description: "",
            isotopes: [
                {
                    number: 31,
                    radioactive: false,
                    weight: 30.973761998,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 16,
        color: "pink",
        name: "sulfur",
        title: {
            en: "Sulfur",
            de: "Schwefel",
            fr: "Soufre",
        },
        letter: "S",
        conventionalWeight: 32.06,
        weight: "[32.059, 32.076]",
        firstIonization: 10.36,
        electronegativity: "2.5",
        info: {
            image: "images/sulfur.jpg",
            link: "pdf-elements/sulfur.pdf",
            imageText:
                "Bird migration patterns have been studied by analyzing sulfur isotope amount ratios in feathers. The Double-crested Cormorant (Phalacrocorax auritus) is an example of a bird species that has been studied using this method.",
            description: "",
            isotopes: [
                {
                    number: 32,
                    radioactive: false,
                    weight: 31.972071174,
                    percent: 94.99,
                    moleFraction: [
                        {
                            fractionLower: 0.9441,
                            fractionUpper: 0.9529,
                        },
                    ],
                },
                {
                    number: 34,
                    radioactive: false,
                    weight: 33.967867,
                    percent: 4.25,
                    moleFraction: [
                        {
                            fractionLower: 0.0396,
                            fractionUpper: 0.0477,
                        },
                    ],
                },
                {
                    number: 33,
                    radioactive: false,
                    weight: 32.97145891,
                    percent: 0.75,
                    moleFraction: [
                        {
                            fractionLower: 0.00729,
                            fractionUpper: 0.00797,
                        },
                    ],
                },
                {
                    number: 36,
                    radioactive: false,
                    weight: 35.967081,
                    percent: 0.01,
                    moleFraction: [
                        {
                            fractionLower: 0.000129,
                            fractionUpper: 0.000187,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 17,
        color: "pink",
        name: "chlorine",
        title: {
            en: "Chlorine",
            de: "Chlor",
            fr: "Chlore",
        },
        letter: "Cl",
        conventionalWeight: 35.45,
        weight: "[35.446, 35.457]",
        firstIonization: 12.95,
        electronegativity: "3.0",
        info: {
            image: "images/chlorine.jpg",
            link: "pdf-elements/chlorine.pdf",
            imageText:
                "By analyzing the isotopic composition of chlorine and oxygen in perchlorate in groundwaters of Long Island, NY, sources of perchlorate contamination could be identified.",
            description: "",
            isotopes: [
                {
                    number: 35,
                    radioactive: false,
                    weight: 34.9688527,
                    percent: 75.76,
                    moleFraction: [
                        {
                            fractionLower: 0.755,
                            fractionUpper: 0.761,
                        },
                    ],
                },
                {
                    number: 37,
                    radioactive: false,
                    weight: 36.9659026,
                    percent: 24.24,
                    moleFraction: [
                        {
                            fractionLower: 0.239,
                            fractionUpper: 0.245,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 18,
        color: "pink",
        name: "argon",
        title: {
            en: "Argon",
            de: "Argon",
            fr: "Argon",
        },
        letter: "Ar",
        conventionalWeight: 39.95,
        weight: "[39.792, 39.963]",
        firstIonization: 15.75,
        info: {
            image: "images/argon.jpg",
            link: "pdf-elements/argon.pdf",
            imageText:
                "Studying the ratios of argon isotopes can provide insight into the origins and movement of magma and the ages of volcanic rock.",
            description: "",
            isotopes: [
                {
                    number: 40,
                    radioactive: false,
                    weight: 39.96238312,
                    percent: 99.6035,
                    moleFraction: [
                        {
                            fractionLower: 0.936,
                            fractionUpper: 1,
                        },
                    ],
                },
                {
                    number: 36,
                    radioactive: false,
                    weight: 35.9675451,
                    percent: 0.3336,
                    moleFraction: [
                        {
                            fractionLower: 0.0,
                            fractionUpper: 0.0208,
                        },
                    ],
                },
                {
                    number: 38,
                    radioactive: false,
                    weight: 37.962732,
                    percent: 0.0629,
                    moleFraction: [
                        {
                            fractionLower: 0.0,
                            fractionUpper: 0.0433,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 19,
        color: "yellow",
        name: "potassium",
        title: {
            en: "Potassium",
            de: "Kalium",
            fr: "Potassium",
        },
        letter: "K",
        weight: "39.0983(1)",
        firstIonization: 4.34,
        electronegativity: "0.8",
        info: {
            image: "images/potassium.jpg",
            link: "pdf-elements/potassium.pdf",
            imageText:
                "The mole ratio n(<sup>40</sup>K)/n(<sup>40</sup>Ar) is used in potassium-argon dating by geologists, archaeologists, and paleoanthropologists to determine the age of rocks",
            description: "",
            isotopes: [
                {
                    number: 39,
                    radioactive: false,
                    weight: 38.96370649,
                    percent: 93.2581,
                    moleFraction: [
                        {
                            fractionLower: 0.932581,
                            fractionUpper: 0.932581,
                        },
                    ],
                },
                {
                    number: 41,
                    radioactive: false,
                    weight: 40.96182526,
                    percent: 6.7302,
                    moleFraction: [
                        {
                            fractionLower: 0.067302,
                            fractionUpper: 0.067302,
                        },
                    ],
                },
                {
                    number: 40,
                    radioactive: true,
                    weight: 39.9639982,
                    percent: 0.0117,
                    moleFraction: [
                        {
                            fractionLower: 0.000117,
                            fractionUpper: 0.000117,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 20,
        color: "yellow",
        name: "calcium",
        title: {
            en: "Calcium",
            de: "Calcium",
            fr: "Calcium",
        },
        letter: "Ca",
        weight: "40.078(4)",
        firstIonization: 6.113,
        electronegativity: "1.0",
        info: {
            image: "images/calcium.jpg",
            link: "pdf-elements/calcium.pdf",
            imageText:
                "The isotope-amount ratio <i>n</i>(<sup>44</sup>Ca)/<i>n</i>(<sup>40</sup>Ca) is used to quantify the calcium cycle (sources and sinks of calcium) in the ocean",
            description: "",
            isotopes: [
                {
                    number: 40,
                    radioactive: false,
                    weight: 39.9625909,
                    percent: 96.941,
                },
                {
                    number: 44,
                    radioactive: false,
                    weight: 43.955482,
                    percent: 2.086,
                },
                {
                    number: 42,
                    radioactive: false,
                    weight: 41.958618,
                    percent: 0.647,
                },
                {
                    number: 48,
                    radioactive: true,
                    weight: 47.9525228,
                    percent: 0.187,
                },
                {
                    number: 43,
                    radioactive: false,
                    weight: 42.958766,
                    percent: 0.135,
                },
                {
                    number: 46,
                    radioactive: false,
                    weight: 45.95369,
                    percent: 0.004,
                },
            ],
        },
    },
    {
        number: 21,
        color: "blue",
        name: "scandium",
        title: {
            en: "Scandium",
            de: "Scandium",
            fr: "Scandium",
        },
        letter: "Sc",
        weight: "44.955908(5)",
        firstIonization: 6.561,
        electronegativity: "1.3",
        info: {
            image: "images/scandium.jpg",
            link: "pdf-elements/scandium.pdf",
            imageText:
                "Sediment flow into an estuary in Argentina after flooding. The radioactive isotope <sup>46</sup>Sc can be used to determine the transportation of sediments by water flow in rivers, estuaries, harbors, and seas.",
            description: "",
            isotopes: [
                {
                    number: 45,
                    radioactive: false,
                    weight: 44.955908,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 22,
        color: "yellow",
        name: "titanium",
        title: {
            en: "Titanium",
            de: "Titan",
            fr: "Titane",
        },
        letter: "Ti",
        weight: "47.867(1)",
        firstIonization: 6.828,
        electronegativity: "1.5",
        info: {
            image: "images/titanium.jpg",
            link: "pdf-elements/titanium.pdf",
            imageText:
                "The <i>n</i>(<sup>50</sup>Ti)/<i>n</i>(<sup>46</sup>Ti) ratio measurements are used to study the early history of the solar system. The value of the ratio can help determine whether the solar system was created from a well-homogenized source.",
            description: "",
            isotopes: [
                {
                    number: 48,
                    radioactive: false,
                    weight: 47.947942,
                    percent: 73.72,
                },
                {
                    number: 46,
                    radioactive: false,
                    weight: 45.952628,
                    percent: 8.25,
                },
                {
                    number: 47,
                    radioactive: false,
                    weight: 46.951759,
                    percent: 7.44,
                },
                {
                    number: 49,
                    radioactive: false,
                    weight: 48.947866,
                    percent: 5.41,
                },
                {
                    number: 50,
                    radioactive: false,
                    weight: 49.944787,
                    percent: 5.18,
                },
            ],
        },
    },
    {
        number: 23,
        color: "yellow",
        name: "vanadium",
        title: {
            en: "Vanadium",
            de: "Vanadium",
            fr: "Vanadium",
        },
        letter: "V",
        weight: "50.9415(1)",
        firstIonization: 6.746,
        electronegativity: "1.6",
        info: {
            image: "images/vanadium.jpg",
            link: "pdf-elements/vanadium.pdf",
            imageText:
                "The isotopic abundances of <sup>50</sup>V and <sup>51</sup>V have been used as an indicator of planetary core formation processes.",
            description: "",
            isotopes: [
                {
                    number: 51,
                    radioactive: false,
                    weight: 50.943957,
                    percent: 99.75,
                },
                {
                    number: 50,
                    radioactive: true,
                    weight: 49.947156,
                    percent: 0.25,
                },
            ],
        },
    },
    {
        number: 24,
        color: "yellow",
        name: "chromium",
        title: {
            en: "Chromium",
            de: "Chrom",
            fr: "Chrome",
        },
        letter: "Cr",
        weight: "51.9961(6)",
        firstIonization: 6.766,
        electronegativity: "1.6",
        info: {
            image: "images/chromium.jpg",
            link: "pdf-elements/chromium.pdf",
            imageText:
                "The early solar nebula was divided into two components; one contained light chromium isotopes and the other contained heavy chromium isotopes. ",
            description: "",
            isotopes: [
                {
                    number: 52,
                    radioactive: false,
                    weight: 51.940506,
                    percent: 83.789,
                },
                {
                    number: 53,
                    radioactive: false,
                    weight: 52.940648,
                    percent: 9.501,
                },
                {
                    number: 50,
                    radioactive: false,
                    weight: 49.946042,
                    percent: 4.345,
                },
                {
                    number: 54,
                    radioactive: false,
                    weight: 53.938879,
                    percent: 2.365,
                },
            ],
        },
    },
    {
        number: 25,
        color: "blue",
        name: "manganese",
        title: {
            en: "Manganese",
            de: "Mangan",
            fr: "Manganèse",
        },
        letter: "Mn",
        weight: "54.938043(2)",
        firstIonization: 7.434,
        electronegativity: "1.5",
        info: {
            image: "images/manganese.jpg",
            link: "pdf-elements/manganese.pdf",
            imageText:
                "<sup>54</sup>Mn could be used in Coal Mining effluents to study migration of heavy metal components.",
            description: "",
            isotopes: [
                {
                    number: 55,
                    radioactive: false,
                    weight: 54.938044,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 26,
        color: "yellow",
        name: "iron",
        title: {
            en: "Iron",
            de: "Eisen",
            fr: "Fer",
        },
        letter: "Fe",
        weight: "55.845(2)",
        firstIonization: 7.902,
        electronegativity: "1.8",
        info: {
            image: "images/iron.jpg",
            link: "pdf-elements/iron.pdf",
            imageText:
                "<sup>52</sup>Fe, with a half-life of 8.2 h, is a positron emitter that can be used in PET studies. It can be produced in a cyclotron from stable <sup>50</sup>Cr by alpha particle capture.",
            description: "",
            isotopes: [
                {
                    number: 56,
                    radioactive: false,
                    weight: 55.934936,
                    percent: 91.754,
                },
                {
                    number: 54,
                    radioactive: false,
                    weight: 53.939609,
                    percent: 5.845,
                },
                {
                    number: 57,
                    radioactive: false,
                    weight: 56.935393,
                    percent: 2.119,
                },
                {
                    number: 58,
                    radioactive: false,
                    weight: 57.933274,
                    percent: 0.282,
                },
            ],
        },
    },
    {
        number: 27,
        color: "blue",
        name: "cobalt",
        title: {
            en: "Cobalt",
            de: "Cobalt",
            fr: "Cobalt",
        },
        letter: "Co",
        weight: "58.933194(4)",
        firstIonization: 7.881,
        electronegativity: "1.8",
        info: {
            image: "images/cobalt.jpg",
            link: "pdf-elements/cobalt.pdf",
            imageText:
                "<sup>60</sup>Co is used to irradiate food sources as a method of preserving food. The gamma radiation from <sup>60</sup>Co kills bacteria and other organisms that cause disease and spoilage of food",
            description: "",
            isotopes: [
                {
                    number: 59,
                    radioactive: false,
                    weight: 58.933194,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 28,
        color: "yellow",
        name: "nickel",
        title: {
            en: "Nickel",
            de: "Nickel",
            fr: "Nickel",
        },
        letter: "Ni",
        weight: "58.6934(4)",
        firstIonization: 7.639,
        electronegativity: "1.8",
        info: {
            image: "images/nickel.jpg",
            link: "pdf-elements/nickel.pdf",
            imageText:
                "<sup>63</sup>Ni, produced from stable <sup>62</sup>Ni, is a beta-emitting radionuclide that serves as an electron source together with <sup>55</sup>Fe in electron-capture detectors. Electron-capture detectors are used as thickness gauges or as detectors for organic analytes in gas chromatography.",
            description: "",
            isotopes: [
                {
                    number: 58,
                    radioactive: false,
                    weight: 57.935342,
                    percent: 68.0769,
                },
                {
                    number: 60,
                    radioactive: false,
                    weight: 59.930786,
                    percent: 26.2231,
                },
                {
                    number: 62,
                    radioactive: false,
                    weight: 61.928345,
                    percent: 3.6345,
                },
                {
                    number: 61,
                    radioactive: false,
                    weight: 60.931056,
                    percent: 1.1399,
                },
                {
                    number: 64,
                    radioactive: false,
                    weight: 63.927967,
                    percent: 0.9256,
                },
            ],
        },
    },
    {
        number: 29,
        color: "yellow",
        name: "copper",
        title: {
            en: "Copper",
            de: "Kupfer",
            fr: "Cuivre",
        },
        letter: "Cu",
        weight: "63.546(3)",
        firstIonization: 7.726,
        electronegativity: "1.9",
        info: {
            image: "images/copper.jpg",
            link: "pdf-elements/copper.pdf",
            imageText:
                "An illustration of a small-animal positron emission tomography (PET) system that uses the <sup>64</sup>Cu radioisotope to generate a reconstructed image of the animal in a noninvasive manner.",
            description: "",
            isotopes: [
                {
                    number: 63,
                    radioactive: false,
                    weight: 62.929598,
                    percent: 69.15,
                },
                {
                    number: 65,
                    radioactive: false,
                    weight: 64.92779,
                    percent: 30.85,
                },
            ],
        },
    },
    {
        number: 30,
        color: "yellow",
        name: "zinc",
        title: {
            en: "Zinc",
            de: "Zink",
            fr: "Zinc",
        },
        letter: "Zn",
        weight: "65.38(2)",
        firstIonization: 9.394,
        electronegativity: "1.6",
        info: {
            image: "images/zinc.jpg",
            link: "pdf-elements/zinc.pdf",
            imageText:
                "ZnO nanoparticles enriched with <sup>67</sup>Zn are being studied for use as biological/environmental nanotoxicity tracers.",
            description: "",
            isotopes: [
                {
                    number: 64,
                    radioactive: false,
                    weight: 63.929142,
                    percent: 49.17,
                },
                {
                    number: 66,
                    radioactive: false,
                    weight: 65.926034,
                    percent: 27.73,
                },
                {
                    number: 68,
                    radioactive: false,
                    weight: 67.924845,
                    percent: 18.45,
                },
                {
                    number: 67,
                    radioactive: false,
                    weight: 66.927128,
                    percent: 4.04,
                },
                {
                    number: 70,
                    radioactive: false,
                    weight: 69.92532,
                    percent: 0.61,
                },
            ],
        },
    },
    {
        number: 31,
        color: "yellow",
        name: "gallium",
        title: {
            en: "Gallium",
            de: "Gallium",
            fr: "Gallium",
        },
        letter: "Ga",
        weight: "69.723(1)",
        firstIonization: 5.999,
        electronegativity: "1.6",
        info: {
            image: "images/gallium.jpg",
            link: "pdf-elements/gallium.pdf",
            imageText:
                "Gallium-68 generator used to provide those in nuclear medicine with the positron-emitting radionuclide <sup>68</sup>Ga. ",
            description: "",
            isotopes: [
                {
                    number: 69,
                    radioactive: false,
                    weight: 68.925574,
                    percent: 60.108,
                },
                {
                    number: 71,
                    radioactive: false,
                    weight: 70.924703,
                    percent: 39.892,
                },
            ],
        },
    },
    {
        number: 32,
        color: "yellow",
        name: "germanium",
        title: {
            en: "Germanium",
            de: "Germanium",
            fr: "Germanium",
        },
        letter: "Ge",
        weight: "72.630(8)",
        firstIonization: 7.899,
        electronegativity: "1.8",
        info: {
            image: "images/germanium.jpg",
            link: "pdf-elements/germanium.pdf",
            imageText:
                "<sup>68</sup>Ge is used to calibrate PET scanners, which can be used for medical diagnostic procedures.",
            description: "",
            isotopes: [
                {
                    number: 74,
                    radioactive: false,
                    weight: 73.92117776,
                    percent: 36.52,
                },
                {
                    number: 72,
                    radioactive: false,
                    weight: 71.9220758,
                    percent: 27.45,
                },
                {
                    number: 70,
                    radioactive: false,
                    weight: 69.924249,
                    percent: 20.52,
                },
                {
                    number: 76,
                    radioactive: true,
                    weight: 75.9214027,
                    percent: 7.75,
                },
                {
                    number: 73,
                    radioactive: false,
                    weight: 72.923459,
                    percent: 7.76,
                },
            ],
        },
    },
    {
        number: 33,
        color: "blue",
        name: "arsenic",
        title: {
            en: "Arsenic",
            de: "Arsen",
            fr: "Arsenic",
        },
        letter: "As",
        weight: "74.921595(6)",
        firstIonization: 9.788,
        electronegativity: "2.0",
        info: {
            image: "images/arsenic.jpg",
            link: "pdf-elements/arsenic.pdf",
            imageText:
                "Rabbits have been injected with <sup>74</sup>As-labeled arsenate to investigate the biotransformation of arsenate by mammals.",
            description: "",
            isotopes: [
                {
                    number: 75,
                    radioactive: false,
                    weight: 74.921595,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 34,
        color: "yellow",
        name: "selenium",
        title: {
            en: "Selenium",
            de: "Selen",
            fr: "Sélénium",
        },
        letter: "Se",
        weight: "78.971(8)",
        firstIonization: 9.752,
        electronegativity: "2.4",
        info: {
            image: "images/selenium.jpg",
            link: "pdf-elements/selenium.pdf",
            imageText: "Film of radiographic inspection using <sup>75</sup>Se.",
            description: "",
            isotopes: [
                {
                    number: 80,
                    radioactive: false,
                    weight: 79.916522,
                    percent: 49.8,
                },
                {
                    number: 78,
                    radioactive: false,
                    weight: 77.917309,
                    percent: 23.69,
                },
                {
                    number: 76,
                    radioactive: false,
                    weight: 75.9192137,
                    percent: 9.23,
                },
                {
                    number: 82,
                    radioactive: true,
                    weight: 81.9167,
                    percent: 8.82,
                },
                {
                    number: 77,
                    radioactive: false,
                    weight: 76.9192137,
                    percent: 7.6,
                },
                {
                    number: 74,
                    radioactive: false,
                    weight: 73.9224759,
                    percent: 0.86,
                },
            ],
        },
    },
    {
        number: 35,
        color: "pink",
        name: "bromine",
        title: {
            en: "Bromine",
            de: "Brom",
            fr: "Brome",
        },
        letter: "Br",
        conventionalWeight: 79.904,
        weight: "[79.901, 79.907]",
        firstIonization: 11.81,
        electronegativity: "2.8",
        info: {
            image: "images/bromine.jpg",
            link: "pdf-elements/bromine.pdf",
            imageText:
                "This is a depiction of how a subsurface/groundwater tracer test is set up. The tracer cloud is where the bromide solution would be. Samples of water in the tracer cloud are then compared to groundwater samples in the neighboring sample site for the experiment being conducted.",
            description: "",
            isotopes: [
                {
                    number: 79,
                    radioactive: false,
                    weight: 78.918338,
                    percent: 50.69,
                    moleFraction: [
                        {
                            fractionLower: 0.505,
                            fractionUpper: 0.508,
                        },
                    ],
                },
                {
                    number: 81,
                    radioactive: false,
                    weight: 80.91629,
                    percent: 49.31,
                    moleFraction: [
                        {
                            fractionLower: 0.492,
                            fractionUpper: 0.495,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 36,
        color: "yellow",
        name: "krypton",
        title: {
            en: "Krypton",
            de: "Krypton",
            fr: "Krypton",
        },
        letter: "Kr",
        weight: "83.798(2)",
        firstIonization: 13.99,
        electronegativity: "3.0",
        info: {
            image: "images/krypton.jpg",
            link: "pdf-elements/krypton.pdf",
            imageText:
                "<sup>81</sup>Kr can be used to date the groundwater being discharged from springs and wells. The photo shows collection of a <sup>81</sup>Kr sample from an artesian well in Farafra Oasis, Egypt",
            description: "",
            isotopes: [
                {
                    number: 84,
                    radioactive: false,
                    weight: 83.91149773,
                    percent: 56.987,
                },
                {
                    number: 86,
                    radioactive: false,
                    weight: 85.91061063,
                    percent: 17.279,
                },
                {
                    number: 82,
                    radioactive: false,
                    weight: 81.913483,
                    percent: 11.593,
                },
                {
                    number: 83,
                    radioactive: false,
                    weight: 82.914127,
                    percent: 11.5,
                },
                {
                    number: 80,
                    radioactive: false,
                    weight: 79.916378,
                    percent: 2.286,
                },
                {
                    number: 78,
                    radioactive: false,
                    weight: 77.920365,
                    percent: 0.355,
                },
            ],
        },
    },
    {
        number: 37,
        color: "yellow",
        name: "rubidium",
        title: {
            en: "Rubidium",
            de: "Rubidium",
            fr: "Rubidium",
        },
        letter: "Rb",
        weight: "85.4678(3)",
        firstIonization: 4.177,
        electronegativity: "0.8",
        info: {
            image: "images/rubidium.jpg",
            link: "pdf-elements/rubidium.pdf",
            imageText:
                "Strontium bearing minerals, such as: feldsapar, biotite, and muscovite, are often used to determine rubidium-strontium ages.",
            description: "",
            isotopes: [
                {
                    number: 85,
                    radioactive: false,
                    weight: 84.91178974,
                    percent: 72.17,
                },
                {
                    number: 87,
                    radioactive: true,
                    weight: 86.90918053,
                    percent: 27.83,
                },
            ],
        },
    },
    {
        number: 38,
        color: "yellow",
        name: "strontium",
        title: {
            en: "Strontium",
            de: "Strontium",
            fr: "Strontium",
        },
        letter: "Sr",
        weight: "87.62(1)",
        firstIonization: 5.694,
        electronegativity: "1.0",
        info: {
            image: "images/strontium.jpg",
            link: "pdf-elements/strontium.pdf",
            imageText:
                "Stable strontium isotope fractionation is being studied for potential use as a proxy for temperature during coral growth.",
            description: "",
            isotopes: [
                {
                    number: 88,
                    radioactive: false,
                    weight: 87.905613,
                    percent: 82.58,
                },
                {
                    number: 86,
                    radioactive: false,
                    weight: 85.909261,
                    percent: 9.86,
                },
                {
                    number: 87,
                    radioactive: false,
                    weight: 86.908878,
                    percent: 7.0,
                },
                {
                    number: 84,
                    radioactive: false,
                    weight: 83.913419,
                    percent: 0.56,
                },
            ],
        },
    },
    {
        number: 39,
        color: "blue",
        name: "yttrium",
        title: {
            en: "Yttrium",
            de: "Yttrium",
            fr: "Yttrium",
        },
        letter: "Y",
        weight: "88.90584(2)",
        firstIonization: 6.217,
        electronegativity: "1.2",
        info: {
            image: "images/yttrium.jpg",
            link: "pdf-elements/yttrium.pdf",
            imageText:
                "Ultrapure <sup>90</sup>Y. Radiomicrosphere therapy (RT) that uses <sup>90</sup>Y microspheres is a proven therapy that helps treat hepatic (liver) cancer.",
            description: "",
            isotopes: [
                {
                    number: 89,
                    radioactive: false,
                    weight: 88.90584,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 40,
        color: "yellow",
        name: "zirconium",
        title: {
            en: "Zirconium",
            de: "Zirconium",
            fr: "Zirconium",
        },
        letter: "Zr",
        weight: "91.224(2)",
        firstIonization: 6.633,
        electronegativity: "1.4",
        info: {
            image: "images/zirconium.jpg",
            link: "pdf-elements/zirconium.pdf",
            imageText:
                "The cores of nuclear reactors have fuel pins that are typically made of uranium-oxide. To keep fission products from escaping into the coolant, these pins are surrounded by a zirconium clad.",
            description: "",
            isotopes: [
                {
                    number: 90,
                    radioactive: false,
                    weight: 89.9047,
                    percent: 51.45,
                },
                {
                    number: 94,
                    radioactive: false,
                    weight: 93.90631,
                    percent: 17.38,
                },
                {
                    number: 92,
                    radioactive: false,
                    weight: 91.90503,
                    percent: 17.15,
                },
                {
                    number: 91,
                    radioactive: false,
                    weight: 90.90564,
                    percent: 11.22,
                },
                {
                    number: 96,
                    radioactive: true,
                    weight: 95.90827,
                    percent: 2.8,
                },
            ],
        },
    },
    {
        number: 41,
        color: "blue",
        name: "niobium",
        title: {
            en: "Niobium",
            de: "Niob",
            fr: "Niobium",
        },
        letter: "Nb",
        weight: "92.90637(2)",
        firstIonization: 6.758,
        electronegativity: "1.6",
        info: {
            image: "images/niobium.jpg",
            link: "pdf-elements/niobium.pdf",
            imageText:
                "Nuclear physicists are trying to study the generation of new isotopes and their elements in stars(astrophysical nucleosynthesis).",
            description: "",
            isotopes: [
                {
                    number: 93,
                    radioactive: false,
                    weight: 92.90637,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 42,
        color: "yellow",
        name: "molybdenum",
        title: {
            en: "Molybdenum",
            de: "Molybdän",
            fr: "Molybdène",
        },
        letter: "Mo",
        weight: "95.95(1)",
        firstIonization: 7.092,
        electronegativity: "1.8",
        info: {
            image: "images/molybdenum.jpg",
            link: "pdf-elements/molybdenum.pdf",
            imageText:
                "Pictured above is Brookhaven National Laboratory where the <i>n</i>(<sup>99</sup>Mo)/<i>n</i>(<sup>99m</sup>Tc) ratio generator was originally developed in the early 1960’s.",
            description: "",
            isotopes: [
                {
                    number: 98,
                    radioactive: false,
                    weight: 97.905405,
                    percent: 24.292,
                },
                {
                    number: 96,
                    radioactive: false,
                    weight: 95.904676,
                    percent: 16.673,
                },
                {
                    number: 95,
                    radioactive: false,
                    weight: 94.905839,
                    percent: 15.873,
                },
                {
                    number: 92,
                    radioactive: false,
                    weight: 91.906808,
                    percent: 14.649,
                },
                {
                    number: 100,
                    radioactive: true,
                    weight: 99.907472,
                    percent: 9.744,
                },
                {
                    number: 97,
                    radioactive: false,
                    weight: 96.906018,
                    percent: 9.582,
                },
                {
                    number: 94,
                    radioactive: false,
                    weight: 93.905085,
                    percent: 9.187,
                },
            ],
        },
    },
    {
        number: 43,
        color: "white",
        name: "technetium",
        firstIonization: 7.28,
        electronegativity: "1.9",
        title: {
            en: "Technetium",
            de: "Technetium",
            fr: "Technétium",
        },
        letter: "Tc",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/technetium.jpg",
            link: "pdf-elements/technetium.pdf",
            imageText:
                "This is a single positron emission computed tomography (SPECT CT) machine. <sup>99m</sup>Tc radiopharmaceuticals absorb to a variety of metastases (tumors). These tumors can be imaged using SPECT and non-invasive computed tomography (CT)",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 44,
        color: "yellow",
        name: "ruthenium",
        title: {
            en: "Ruthenium",
            de: "Ruthenium",
            fr: "Ruthénium",
        },
        letter: "Ru",
        weight: "101.07(2)",
        firstIonization: 7.36,
        electronegativity: "2.2",
        info: {
            image: "images/ruthenium.jpg",
            link: "pdf-elements/ruthenium.pdf",
            imageText:
                "The 88-Inch cyclotron can be used to produce both light- and heavy-ions. <sup>104</sup>Ru is used to produce radioisotope <sup>105</sup>Rh. <sup>105</sup>Rh has been studied for use in the treatment of bone pain. ",
            description: "",
            isotopes: [
                {
                    number: 102,
                    radioactive: false,
                    weight: 101.904344,
                    percent: 31.55,
                },
                {
                    number: 104,
                    radioactive: false,
                    weight: 103.90543,
                    percent: 18.62,
                },
                {
                    number: 101,
                    radioactive: false,
                    weight: 100.905577,
                    percent: 17.06,
                },
                {
                    number: 99,
                    radioactive: false,
                    weight: 98.905934,
                    percent: 12.76,
                },
                {
                    number: 100,
                    radioactive: false,
                    weight: 99.904214,
                    percent: 12.6,
                },
                {
                    number: 96,
                    radioactive: false,
                    weight: 95.90759,
                    percent: 5.54,
                },
                {
                    number: 98,
                    radioactive: false,
                    weight: 97.90529,
                    percent: 1.87,
                },
            ],
        },
    },
    {
        number: 45,
        color: "blue",
        name: "rhodium",
        title: {
            en: "Rhodium",
            de: "Rhodium",
            fr: "Rhodium",
        },
        letter: "Rh",
        weight: "102.90549(2)",
        firstIonization: 7.456,
        electronegativity: "2.2",
        info: {
            image: "images/rhodium.jpg",
            link: "pdf-elements/rhodium.pdf",
            imageText:
                "Bone cancer cells that have been pap stained and magnified to 400 times. The beta particles of <sup>105</sup>Rh are used in target radiotherapy to kill the cancer cells or cause cancer cell sterilization.",
            description: "",
            isotopes: [
                {
                    number: 103,
                    radioactive: false,
                    weight: 102.9055,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 46,
        color: "yellow",
        name: "palladium",
        title: {
            en: "Palladium",
            de: "Palladium",
            fr: "Palladium",
        },
        letter: "Pd",
        weight: "106.42(1)",
        firstIonization: 9.336,
        electronegativity: "2.2",
        info: {
            image: "images/palladium.jpg",
            link: "pdf-elements/palladium.pdf",
            imageText:
                "The Crab Nebula seen above is an example of an exploding star, which is responsible for the release of heavy elements like <sup>107</sup>Pd into space.",
            description: "",
            isotopes: [
                {
                    number: 106,
                    radioactive: false,
                    weight: 105.90348,
                    percent: 27.33,
                },
                {
                    number: 108,
                    radioactive: false,
                    weight: 107.903892,
                    percent: 26.46,
                },
                {
                    number: 105,
                    radioactive: false,
                    weight: 104.90508,
                    percent: 22.33,
                },
                {
                    number: 110,
                    radioactive: false,
                    weight: 109.905172,
                    percent: 11.72,
                },
                {
                    number: 104,
                    radioactive: false,
                    weight: 103.904031,
                    percent: 11.14,
                },
                {
                    number: 102,
                    radioactive: false,
                    weight: 101.9056,
                    percent: 1.02,
                },
            ],
        },
    },
    {
        number: 47,
        color: "yellow",
        name: "silver",
        title: {
            en: "Silver",
            de: "Silber",
            fr: "Argent",
        },
        letter: "Ag",
        weight: "107.8682(2)",
        firstIonization: 7.576,
        electronegativity: "1.9",
        info: {
            image: "images/silver.jpg",
            link: "pdf-elements/silver.pdf",
            imageText:
                "Due to the isotopic signatures of the silver (Ag), copper (Cu) and lead (Pb) in the metal used to make Spanish coins, the origin of the metals can be traced to help determine the flow of metal in the global market during the 16th century.",
            description: "",
            isotopes: [
                {
                    number: 107,
                    radioactive: false,
                    weight: 106.90509,
                    percent: 51.839,
                },
                {
                    number: 109,
                    radioactive: false,
                    weight: 108.904755,
                    percent: 48.161,
                },
            ],
        },
    },
    {
        number: 48,
        color: "yellow",
        name: "cadmium",
        title: {
            en: "Cadmium",
            de: "Cadmium",
            fr: "Cadmium",
        },
        letter: "Cd",
        weight: "112.414(4)",
        firstIonization: 8.993,
        electronegativity: "1.7",
        info: {
            image: "images/cadmium.jpg",
            link: "pdf-elements/cadmium.pdf",
            imageText:
                "Heavy-metal accumulation is a threat to our world’s water systems and wildlife. As a way measure the influence heavy metals on wildlife, some researchers use animal food enriched in specific cadmium isotopes. This information is used to measure bio-uptake and accumulation rates of metals in an exposed animal.",
            description: "",
            isotopes: [
                {
                    number: 114,
                    radioactive: false,
                    weight: 113.903365,
                    percent: 28.754,
                },
                {
                    number: 112,
                    radioactive: false,
                    weight: 111.902763,
                    percent: 24.109,
                },
                {
                    number: 111,
                    radioactive: false,
                    weight: 110.904183,
                    percent: 12.795,
                },
                {
                    number: 110,
                    radioactive: false,
                    weight: 109.903007,
                    percent: 12.47,
                },
                {
                    number: 113,
                    radioactive: true,
                    weight: 112.904408,
                    percent: 12.227,
                },
                {
                    number: 116,
                    radioactive: true,
                    weight: 115.904763,
                    percent: 7.512,
                },
                {
                    number: 106,
                    radioactive: false,
                    weight: 105.90646,
                    percent: 1.245,
                },
                {
                    number: 108,
                    radioactive: false,
                    weight: 107.904183,
                    percent: 0.888,
                },
            ],
        },
    },
    {
        number: 49,
        color: "yellow",
        name: "indium",
        title: {
            en: "Indium",
            de: "Indium",
            fr: "Indium",
        },
        letter: "In",
        weight: "114.818(1)",
        firstIonization: 5.786,
        electronegativity: "1.7",
        info: {
            image: "images/indium.jpg",
            link: "pdf-elements/indium.pdf",
            imageText:
                "Radionuclide Imaging of Infection. Leukocytes are white blood cells in the body that protect the body from infection and can be dyed with <sup>111</sup>In to locate the site of an infection in the body.",
            description: "",
            isotopes: [
                {
                    number: 115,
                    radioactive: true,
                    weight: 114.90387878,
                    percent: 95.719,
                },
                {
                    number: 113,
                    radioactive: false,
                    weight: 112.904062,
                    percent: 4.281,
                },
            ],
        },
    },
    {
        number: 50,
        color: "yellow",
        name: "tin",
        title: {
            en: "Tin",
            de: "Zinn",
            fr: "Etain",
        },
        letter: "Sn",
        weight: "118.710(7)",
        firstIonization: 7.343,
        electronegativity: "1.8",
        info: {
            image: "images/tin.jpg",
            link: "pdf-elements/tin.pdf",
            imageText:
                "<sup>117m</sup>Sn DTPA is used routinely for diagnostic bone imaging and treatment of bone pain caused by the spread of cancer to bones. By using <sup>117m</sup>Sn DTPA, marrow toxicity can be reduced and the therapeutic efficacy of using radionuclides is maintained.",
            description: "",
            isotopes: [
                {
                    number: 120,
                    radioactive: false,
                    weight: 119.902202,
                    percent: 32.58,
                },
                {
                    number: 118,
                    radioactive: false,
                    weight: 117.901607,
                    percent: 24.22,
                },
                {
                    number: 116,
                    radioactive: false,
                    weight: 115.901743,
                    percent: 14.54,
                },
                {
                    number: 119,
                    radioactive: false,
                    weight: 118.903311,
                    percent: 8.59,
                },
                {
                    number: 117,
                    radioactive: false,
                    weight: 116.902954,
                    percent: 7.68,
                },
                {
                    number: 124,
                    radioactive: false,
                    weight: 123.905277,
                    percent: 5.79,
                },
                {
                    number: 122,
                    radioactive: false,
                    weight: 121.90344,
                    percent: 4.63,
                },
                {
                    number: 112,
                    radioactive: false,
                    weight: 111.904824,
                    percent: 0.97,
                },
                {
                    number: 114,
                    radioactive: false,
                    weight: 113.902783,
                    percent: 0.66,
                },
                {
                    number: 115,
                    radioactive: false,
                    weight: 114.9033447,
                    percent: 0.34,
                },
            ],
        },
    },
    {
        number: 51,
        color: "yellow",
        name: "antimony",
        title: {
            en: "Antimony",
            de: "Antimon",
            fr: "Antimoine",
        },
        letter: "Sb",
        weight: "121.760(1)",
        firstIonization: 8.608,
        electronegativity: "1.9",
        info: {
            image: "images/antimony.jpg",
            link: "pdf-elements/antimony.pdf",
            imageText:
                "<sup>123</sup>Sb can be used to produce <sup>124</sup>I, which is used in radio-immunotherapy and also in positron emission tomography.",
            description: "",
            isotopes: [
                {
                    number: 121,
                    radioactive: false,
                    weight: 120.90381,
                    percent: 57.21,
                },
                {
                    number: 123,
                    radioactive: false,
                    weight: 122.90421,
                    percent: 42.79,
                },
            ],
        },
    },
    {
        number: 52,
        color: "yellow",
        name: "tellurium",
        title: {
            en: "Tellurium",
            de: "Tellur",
            fr: "Tellure",
        },
        letter: "Te",
        weight: "127.60(3)",
        firstIonization: 9.009,
        electronegativity: "2.1",
        info: {
            image: "images/tellurium.jpg",
            link: "pdf-elements/tellurium.pdf",
            imageText:
                "Tellurium isotopes are useful for studying the contribution of stellar products to the molecular cloud from which the Sun and planets were formed.",
            description: "",
            isotopes: [
                {
                    number: 130,
                    radioactive: true,
                    weight: 129.90622275,
                    percent: 34.08,
                },
                {
                    number: 128,
                    radioactive: true,
                    weight: 127.904461,
                    percent: 31.74,
                },
                {
                    number: 126,
                    radioactive: false,
                    weight: 125.90331,
                    percent: 18.84,
                },
                {
                    number: 125,
                    radioactive: false,
                    weight: 124.90443,
                    percent: 7.07,
                },
                {
                    number: 124,
                    radioactive: false,
                    weight: 123.90282,
                    percent: 4.74,
                },
                {
                    number: 122,
                    radioactive: false,
                    weight: 121.90304,
                    percent: 2.55,
                },
                {
                    number: 123,
                    radioactive: false,
                    weight: 122.90427,
                    percent: 0.89,
                },
                {
                    number: 120,
                    radioactive: false,
                    weight: 119.90406,
                    percent: 0.09,
                },
            ],
        },
    },
    {
        number: 53,
        color: "blue",
        name: "iodine",
        title: {
            en: "Iodine",
            de: "Iod",
            fr: "Iode",
        },
        letter: "I",
        weight: "126.90447(3)",
        firstIonization: 10.45,
        electronegativity: "2.5",
        info: {
            image: "images/iodine.jpg",
            link: "pdf-elements/iodine.pdf",
            imageText:
                "Global distribution of <sup>129</sup>I before the Fukushima disaster in Japan.",
            description: "",
            isotopes: [
                {
                    number: 127,
                    radioactive: false,
                    weight: 126.90447,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 54,
        color: "yellow",
        name: "xenon",
        title: {
            en: "Xenon",
            de: "Xenon",
            fr: "Xénon",
        },
        letter: "Xe",
        weight: "131.293(6)",
        firstIonization: 12.12,
        electronegativity: "2.6",
        info: {
            image: "images/xenon.jpg",
            link: "pdf-elements/xenon.pdf",
            imageText:
                "Monitoring of <sup>133</sup>Xe in air at Freiburg, Germany. Persistent low levels of anthropogenic <sup>133</sup>Xe that are generally attributable to normal acceptable releases from nuclear power plants with variability related in part to multiple sources and changing wind patterns. ",
            description: "",
            isotopes: [
                {
                    number: 132,
                    radioactive: false,
                    weight: 131.90415509,
                    percent: 26.909,
                },
                {
                    number: 129,
                    radioactive: false,
                    weight: 128.90478086,
                    percent: 26.401,
                },
                {
                    number: 131,
                    radioactive: false,
                    weight: 130.905084,
                    percent: 21.232,
                },
                {
                    number: 134,
                    radioactive: false,
                    weight: 133.905395,
                    percent: 10.436,
                },
                {
                    number: 136,
                    radioactive: true,
                    weight: 135.90721448,
                    percent: 8.857,
                },
                {
                    number: 130,
                    radioactive: false,
                    weight: 129.9035094,
                    percent: 4.071,
                },
                {
                    number: 128,
                    radioactive: false,
                    weight: 127.903531,
                    percent: 1.91,
                },
                {
                    number: 124,
                    radioactive: false,
                    weight: 123.90589,
                    percent: 0.095,
                },
                {
                    number: 126,
                    radioactive: false,
                    weight: 125.9043,
                    percent: 0.089,
                },
            ],
        },
    },
    {
        number: 55,
        color: "blue",
        name: "caesium",
        title: {
            en: "Caesium",
            de: "Caesium",
            fr: "Césium",
        },
        letter: "Cs",
        weight: "132.90545196(6)",
        firstIonization: 3.893,
        electronegativity: "0.7",
        info: {
            image: "images/caesium.jpg",
            link: "pdf-elements/caesium.pdf",
            imageText:
                "Spatial analysis of cesium in sediments of Watts Bar Reservoir.",
            description: "",
            isotopes: [
                {
                    number: 133,
                    radioactive: false,
                    weight: 132.90545196,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 56,
        color: "yellow",
        name: "barium",
        title: {
            en: "Barium",
            de: "Barium",
            fr: "Baryum",
        },
        letter: "Ba",
        weight: "137.327(7)",
        firstIonization: 5.211,
        electronegativity: "0.9",
        info: {
            image: "images/barium.jpg",
            link: "pdf-elements/barium.pdf",
            imageText:
                "The crab nebula seen above is an example of an exploding star, which is responsible for the release of heavy elements into space.",
            description: "",
            isotopes: [
                {
                    number: 138,
                    radioactive: false,
                    weight: 137.905247,
                    percent: 71.7,
                },
                {
                    number: 137,
                    radioactive: false,
                    weight: 136.905827,
                    percent: 11.23,
                },
                {
                    number: 136,
                    radioactive: false,
                    weight: 135.904576,
                    percent: 7.85,
                },
                {
                    number: 135,
                    radioactive: false,
                    weight: 134.905688,
                    percent: 6.59,
                },
                {
                    number: 134,
                    radioactive: false,
                    weight: 133.904508,
                    percent: 2.42,
                },
                {
                    number: 130,
                    radioactive: true,
                    weight: 129.90632,
                    percent: 0.11,
                },
                {
                    number: 132,
                    radioactive: true,
                    weight: 131.905061,
                    percent: 0.1,
                },
            ],
        },
    },
    {
        number: 57,
        color: "yellow",
        name: "lanthanum",
        title: {
            en: "Lanthanum",
            de: "Lanthan",
            fr: "Lanthane",
        },
        letter: "La",
        weight: "138.90547(7)",
        firstIonization: 5.577,
        electronegativity: "1.1",
        info: {
            image: "images/lanthanum.jpg",
            link: "pdf-elements/lanthanum.pdf",
            imageText:
                "Studies have shown that <sup>138</sup>La can be used as part of a nuclear cosmochronometer along with <sup>138</sup>Ce and <sup>136</sup>Ce to measure time elapsed from a supernova explosion producing large numbers of neutrinos.",
            description: "",
            isotopes: [
                {
                    number: 139,
                    radioactive: false,
                    weight: 138.90636,
                    percent: 99.91119,
                },
                {
                    number: 138,
                    radioactive: true,
                    weight: 137.90712,
                    percent: 0.08881,
                },
            ],
        },
    },
    {
        number: 58,
        color: "yellow",
        name: "cerium",
        title: {
            en: "Cerium",
            de: "Cer",
            fr: "Cérium",
        },
        letter: "Ce",
        weight: "140.116(1)",
        firstIonization: 5.538,
        electronegativity: "1.1",
        info: {
            image: "images/cerium.jpg",
            link: "pdf-elements/cerium.pdf",
            imageText:
                "<sup>138</sup>La–<sup>138</sup>Ce and <sup>147</sup>Sm–<sup>143</sup>Nd are two decay systems that, when combined, are useful for studying processes affecting the light-rare-earth elements and the igneous evolution of the moon and Earth.",
            description: "",
            isotopes: [
                {
                    number: 140,
                    radioactive: false,
                    weight: 139.90544,
                    percent: 88.449,
                },
                {
                    number: 142,
                    radioactive: false,
                    weight: 141.90925,
                    percent: 11.114,
                },
                {
                    number: 136,
                    radioactive: false,
                    weight: 135.907129,
                    percent: 0.186,
                },
                {
                    number: 138,
                    radioactive: false,
                    weight: 137.90599,
                    percent: 0.251,
                },
            ],
        },
    },
    {
        number: 59,
        color: "blue",
        name: "praseodymium",
        title: {
            en: "Praseodymium",
            de: "Praseodym",
            fr: "Praséodyme",
        },
        letter: "Pr",
        weight: "140.90766(1)",
        firstIonization: 5.464,
        electronegativity: "1.1",
        info: {
            image: "images/praseodymium.jpg",
            link: "pdf-elements/praseodymium.pdf",
            imageText:
                "Glass microcapillary for use in brachytherapy investigations, composed of a mixture of silica, aluminium, oxygen, and praseodymium-141 and praseodymium-142. The glass was irradiated in a nuclear reactor to produce radioactive <sup>142</sup>Pr from stable <sup>141</sup>Pr.",
            description: "",
            isotopes: [
                {
                    number: 141,
                    radioactive: false,
                    weight: 140.90766,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 60,
        color: "yellow",
        name: "neodymium",
        title: {
            en: "Neodymium",
            de: "Neodym",
            fr: "Néodyme",
        },
        letter: "Nd",
        weight: "144.242(3)",
        firstIonization: 5.525,
        electronegativity: "1.1",
        info: {
            image: "images/neodymium.jpg",
            link: "pdf-elements/neodymium.pdf",
            imageText:
                "<sup>143</sup>Nd is a radiogenic isotope produced by decay of <sup>147</sup>Sm, with half-life of 1.06 x 10<sup>11</sup> years. Thus, the ratio of <i>n</i> (<sup>143</sup>Nd)/ <i>n</i> (<sup>144</sup>Nd) can be used for dating rocks on long time scales, and as a chemical tracer in geochemistry.",
            description: "",
            isotopes: [
                {
                    number: 142,
                    radioactive: false,
                    weight: 141.90773,
                    percent: 27.153,
                },
                {
                    number: 144,
                    radioactive: true,
                    weight: 143.91009,
                    percent: 23.798,
                },
                {
                    number: 146,
                    radioactive: false,
                    weight: 145.91312,
                    percent: 17.189,
                },
                {
                    number: 143,
                    radioactive: false,
                    weight: 142.90982,
                    percent: 12.173,
                },
                {
                    number: 145,
                    radioactive: false,
                    weight: 144.91258,
                    percent: 8.293,
                },
                {
                    number: 148,
                    radioactive: false,
                    weight: 147.9169,
                    percent: 5.756,
                },
                {
                    number: 150,
                    radioactive: true,
                    weight: 149.9209,
                    percent: 5.638,
                },
            ],
        },
    },
    {
        number: 61,
        color: "white",
        name: "promethium",
        title: {
            en: "Promethium",
            de: "Promethium",
            fr: "Promethium",
        },
        letter: "Pm",
        weight: "",
        firstIonization: 5.58,
        electronegativity: "1.2",
        noStableIsotope: true,
        info: {
            image: "images/promethium.jpg",
            link: "pdf-elements/promethium.pdf",
            imageText:
                "The beta-ray backscatter technique requires a layer of <sup>147</sup>Pm between the cladding tube and the graphite layer to measure the thickness of the graphite along the inner surface of the cladding tube.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 62,
        color: "yellow",
        name: "samarium",
        title: {
            en: "Samarium",
            de: "Samarium",
            fr: "Samarium",
        },
        letter: "Sm",
        weight: "150.36(2)",
        firstIonization: 5.643,
        electronegativity: "1.2",
        info: {
            image: "images/samarium.jpg",
            link: "pdf-elements/samarium.pdf",
            imageText:
                "Targeting of bone metastases with <sup>153</sup>Sm-EDTMP in a prostate cancer patient. ANT indicates the anterior view of the patient; POST indicates the posterior view of the patient; arrow represents uptake in the pubic bone of the patient",
            description: "",
            isotopes: [
                {
                    number: 152,
                    radioactive: false,
                    weight: 151.91974,
                    percent: 26.74,
                },
                {
                    number: 154,
                    radioactive: false,
                    weight: 153.92222,
                    percent: 22.74,
                },
                {
                    number: 147,
                    radioactive: true,
                    weight: 146.9149,
                    percent: 15.0,
                },
                {
                    number: 149,
                    radioactive: false,
                    weight: 148.91719,
                    percent: 13.82,
                },
                {
                    number: 148,
                    radioactive: true,
                    weight: 147.91483,
                    percent: 11.25,
                },
                {
                    number: 150,
                    radioactive: false,
                    weight: 149.91728,
                    percent: 7.37,
                },
                {
                    number: 144,
                    radioactive: false,
                    weight: 143.91201,
                    percent: 3.08,
                },
            ],
        },
    },
    {
        number: 63,
        color: "yellow",
        name: "europium",
        title: {
            en: "Europium",
            de: "Europium",
            fr: "Europium",
        },
        letter: "Eu",
        weight: "151.964(1)",
        firstIonization: 5.67,
        electronegativity: "1.1",
        info: {
            image: "images/europium.jpg",
            link: "pdf-elements/europium.pdf",
            imageText:
                "<sup>152</sup>Eu is used as a reference source for calibrating gamma-ray spectrometer systems like the one pictured here.",
            description: "",
            isotopes: [
                {
                    number: 153,
                    radioactive: false,
                    weight: 152.92124,
                    percent: 52.19,
                },
                {
                    number: 151,
                    radioactive: true,
                    weight: 150.91986,
                    percent: 47.81,
                },
            ],
        },
    },
    {
        number: 64,
        color: "yellow",
        name: "gadolinium",
        title: {
            en: "Gadolinium",
            de: "Gadolinium",
            fr: "Gadolinium",
        },
        letter: "Gd",
        weight: "157.25(3)",
        firstIonization: 6.15,
        electronegativity: "1.2",
        info: {
            image: "images/gadolinium.jpg",
            link: "pdf-elements/gadolinium.pdf",
            imageText:
                "The addition of <sup>157</sup>Gd to Neutron Capture Therapy (NCT) has been shown to be more effective at targeting tumors than the previous method of applying only <sup>10</sup>B to the treatment.",
            description: "",
            isotopes: [
                {
                    number: 158,
                    radioactive: false,
                    weight: 157.92411,
                    percent: 24.84,
                },
                {
                    number: 160,
                    radioactive: false,
                    weight: 159.92706,
                    percent: 21.86,
                },
                {
                    number: 156,
                    radioactive: false,
                    weight: 155.92213,
                    percent: 20.47,
                },
                {
                    number: 157,
                    radioactive: false,
                    weight: 156.92397,
                    percent: 15.65,
                },
                {
                    number: 155,
                    radioactive: false,
                    weight: 154.92263,
                    percent: 14.8,
                },
                {
                    number: 154,
                    radioactive: false,
                    weight: 153.92087,
                    percent: 2.18,
                },
                {
                    number: 152,
                    radioactive: false,
                    weight: 151.9198,
                    percent: 0.2,
                },
            ],
        },
    },
    {
        number: 65,
        color: "blue",
        name: "terbium",
        title: {
            en: "Terbium",
            de: "Terbium",
            fr: "Terbium",
        },
        letter: "Tb",
        weight: "158.925354(8)",
        firstIonization: 5.863,
        electronegativity: "1.2",
        info: {
            image: "images/terbium.jpg",
            link: "pdf-elements/terbium.pdf",
            imageText:
                "<sup>161</sup>Tb is being studied for imaging purposes as it allows for on-line monitoring of its distribution with gamma cameras.",
            description: "",
            isotopes: [
                {
                    number: 159,
                    radioactive: false,
                    weight: 158.92535,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 66,
        color: "yellow",
        name: "dysprosium",
        title: {
            en: "Dysprosium",
            de: "Dysprosium",
            fr: "Dysprosium",
        },
        letter: "Dy",
        weight: "162.500(1)",
        firstIonization: 5.938,
        electronegativity: "1.2",
        info: {
            image: "images/dysprosium.jpg",
            link: "pdf-elements/dysprosium.pdf",
            imageText:
                "Magneto-Optical Trapping (MOT) of isotopes of dysprosium.",
            description: "",
            isotopes: [
                {
                    number: 164,
                    radioactive: false,
                    weight: 163.92918,
                    percent: 28.26,
                },
                {
                    number: 162,
                    radioactive: false,
                    weight: 161.92681,
                    percent: 25.475,
                },
                {
                    number: 163,
                    radioactive: false,
                    weight: 162.92874,
                    percent: 24.896,
                },
                {
                    number: 161,
                    radioactive: false,
                    weight: 160.92694,
                    percent: 18.889,
                },
                {
                    number: 160,
                    radioactive: false,
                    weight: 159.9252,
                    percent: 2.329,
                },
                {
                    number: 158,
                    radioactive: false,
                    weight: 157.92442,
                    percent: 0.095,
                },
                {
                    number: 156,
                    radioactive: false,
                    weight: 155.92428,
                    percent: 0.056,
                },
            ],
        },
    },
    {
        number: 67,
        color: "blue",
        name: "holmium",
        title: {
            en: "Holmium",
            de: "Holmium",
            fr: "Holmium",
        },
        letter: "Ho",
        weight: "164.930328(7)",
        firstIonization: 6.021,
        electronegativity: "1.2",
        info: {
            image: "images/holmium.jpg",
            link: "pdf-elements/holmium.pdf",
            imageText:
                "Schematic overview of the administration system for <sup>166</sup>Ho-RE (radioembolization)",
            description: "",
            isotopes: [
                {
                    number: 165,
                    radioactive: false,
                    weight: 164.93033,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 68,
        color: "yellow",
        name: "erbium",
        title: {
            en: "Erbium",
            de: "Erbium",
            fr: "Erbium",
        },
        letter: "Er",
        weight: "167.259(3)",
        firstIonization: 6.107,
        electronegativity: "1.2",
        info: {
            image: "images/erbium.jpg",
            link: "pdf-elements/erbium.pdf",
            imageText:
                "<sup>169</sup>Er is used in radiation synovectomy, which is a regularly practiced nuclear medicine therapy, on rheumatoid arthritis patients whose condition is resistant to standard methods of treatment.",
            description: "",
            isotopes: [
                {
                    number: 166,
                    radioactive: false,
                    weight: 165.9303,
                    percent: 33.503,
                },
                {
                    number: 168,
                    radioactive: false,
                    weight: 167.93238,
                    percent: 26.978,
                },
                {
                    number: 167,
                    radioactive: false,
                    weight: 166.93205,
                    percent: 22.869,
                },
                {
                    number: 170,
                    radioactive: false,
                    weight: 169.93547,
                    percent: 14.91,
                },
                {
                    number: 164,
                    radioactive: false,
                    weight: 163.92921,
                    percent: 1.601,
                },
                {
                    number: 162,
                    radioactive: false,
                    weight: 161.92879,
                    percent: 0.139,
                },
            ],
        },
    },
    {
        number: 69,
        color: "blue",
        name: "thulium",
        title: {
            en: "Thulium",
            de: "Thulium",
            fr: "Thulium",
        },
        letter: "Tm",
        weight: "168.934218(6)",
        firstIonization: 6.184,
        electronegativity: "1.2",
        info: {
            image: "images/thulium.jpg",
            link: "pdf-elements/thulium.pdf",
            imageText:
                "Wholebody single-photon emission computed spectroscopy (colored layer in image) and CT scan of Beagle dog recorded after 4 hours of administration of 170Tm-labeled microparticles. Radioactive-colloid accumulation is displayed in the right knee joint. The Beagle did not suffer any health impairment.",
            description: "",
            isotopes: [
                {
                    number: 169,
                    radioactive: false,
                    weight: 168.93422,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 70,
        color: "yellow",
        name: "ytterbium",
        title: {
            en: "Ytterbium",
            de: "Ytterbium",
            fr: "Ytterbium",
        },
        letter: "Yb",
        weight: "173.045(10)",
        firstIonization: 6.254,
        electronegativity: "1.2",
        info: {
            image: "images/ytterbium.jpg",
            link: "pdf-elements/ytterbium.pdf",
            imageText:
                "Inside National Institute of Standards and Technology’s (NIST) new optical atomic clock. The red rings are magnetic coils and the red laser beam is an optical lattice. The intersecting violet lasers cool the ytterbium atoms.",
            description: "",
            isotopes: [
                {
                    number: 174,
                    radioactive: false,
                    weight: 173.93887,
                    percent: 31.896,
                },
                {
                    number: 172,
                    radioactive: false,
                    weight: 171.93639,
                    percent: 21.754,
                },
                {
                    number: 173,
                    radioactive: false,
                    weight: 172.93822,
                    percent: 16.098,
                },
                {
                    number: 171,
                    radioactive: false,
                    weight: 170.93633,
                    percent: 14.216,
                },
                {
                    number: 176,
                    radioactive: false,
                    weight: 175.94258,
                    percent: 12.887,
                },
                {
                    number: 170,
                    radioactive: false,
                    weight: 169.93477,
                    percent: 3.023,
                },
                {
                    number: 168,
                    radioactive: false,
                    weight: 167.93389,
                    percent: 0.126,
                },
            ],
        },
    },
    {
        number: 71,
        color: "yellow",
        name: "lutetium",
        title: {
            en: "Lutetium",
            de: "Lutetium",
            fr: "Lutétium",
        },
        letter: "Lu",
        weight: "174.9668(1)",
        firstIonization: 5.425,
        electronegativity: "1.3",
        info: {
            image: "images/lutetium.jpg",
            link: "pdf-elements/lutetium.pdf",
            imageText:
                "<sup>177</sup>Lu has potential for use as a radio-immunotherapy isotope for the treatment of small, soft tumors and for imaging purposes.",
            description: "",
            isotopes: [
                {
                    number: 175,
                    radioactive: false,
                    weight: 174.94078,
                    percent: 97.401,
                },
                {
                    number: 176,
                    radioactive: true,
                    weight: 175.94269,
                    percent: 2.599,
                },
            ],
        },
    },
    {
        number: 72,
        color: "yellow",
        name: "hafnium",
        title: {
            en: "Hafnium",
            de: "Hafnium",
            fr: "Hafnium",
        },
        letter: "Hf",
        weight: "178.486 ± 0.006",
        firstIonization: 6.825,
        electronegativity: "1.3",
        info: {
            image: "images/hafnium.jpg",
            link: "pdf-elements/hafnium.pdf",
            imageText:
                "Relations between the <i>n</i>(<sup>176</sup>Hf)/<i>n</i>(<sup>177</sup>Hf) and <i>n</i>(<sup>176</sup>Hf)/<i>n</i>(<sup>176</sup>Lu) isotope amount ratios can be used to determine the ages of minerals and rocks.",
            description: "",
            isotopes: [
                {
                    number: 180,
                    radioactive: false,
                    weight: 179.94656,
                    percent: 35.08,
                },
                {
                    number: 178,
                    radioactive: false,
                    weight: 177.94371,
                    percent: 27.28,
                },
                {
                    number: 177,
                    radioactive: false,
                    weight: 176.94323,
                    percent: 18.6,
                },
                {
                    number: 179,
                    radioactive: false,
                    weight: 178.94582,
                    percent: 13.62,
                },
                {
                    number: 176,
                    radioactive: false,
                    weight: 175.94141,
                    percent: 5.26,
                },
                {
                    number: 174,
                    radioactive: true,
                    weight: 173.94005,
                    percent: 0.16,
                },
            ],
        },
    },
    {
        number: 73,
        color: "yellow",
        name: "tantalum",
        title: {
            en: "Tantalum",
            de: "Tantal",
            fr: "Tantale",
        },
        letter: "Ta",
        weight: "180.94788(2)",
        firstIonization: 7.549,
        electronegativity: "1.5",
        info: {
            image: "images/tantalum.jpg",
            link: "pdf-elements/tantalum.pdf",
            imageText:
                "The multiwire gamma camera has a <sup>178</sup>Ta generator incorporated in its housing. This provides portable and laboratory ventricular function assessments for cardiovascular patients.",
            description: "",
            isotopes: [
                {
                    number: 181,
                    radioactive: false,
                    weight: 180.948,
                    percent: 99.98799,
                },
                {
                    number: 180,
                    radioactive: false,
                    weight: 179.94746,
                    percent: 0.01201,
                },
            ],
        },
    },
    {
        number: 74,
        color: "yellow",
        name: "tungsten",
        title: {
            en: "Tungsten",
            de: "Wolfram",
            fr: "Tungstène",
        },
        letter: "W",
        weight: "183.84(1)",
        firstIonization: 7.864,
        electronegativity: "1.7",
        info: {
            image: "images/tungsten.jpg",
            link: "pdf-elements/tungsten.pdf",
            imageText:
                "Core formation scenarios. <sup>182</sup>Hf is produced during the end stages of a supernova explosion and decays to <sup>182</sup>W.",
            description: "",
            isotopes: [
                {
                    number: 184,
                    radioactive: false,
                    weight: 183.950931,
                    percent: 30.64,
                },
                {
                    number: 186,
                    radioactive: false,
                    weight: 185.95436,
                    percent: 28.43,
                },
                {
                    number: 182,
                    radioactive: false,
                    weight: 181.948204,
                    percent: 26.5,
                },
                {
                    number: 183,
                    radioactive: false,
                    weight: 182.950223,
                    percent: 14.31,
                },
                {
                    number: 180,
                    radioactive: true,
                    weight: 179.94671,
                    percent: 0.12,
                },
            ],
        },
    },
    {
        number: 75,
        color: "yellow",
        name: "rhenium",
        title: {
            en: "Rhenium",
            de: "Rhenium",
            fr: "Rhénium",
        },
        letter: "Re",
        weight: "186.207(1)",
        firstIonization: 7.833,
        electronegativity: "1.9",
        info: {
            image: "images/rhenium.jpg",
            link: "pdf-elements/rhenium.pdf",
            imageText:
                "Rhenium-osmium dating method is of special interest for the dating of rhenium-bearing meteorites.",
            description: "",
            isotopes: [
                {
                    number: 187,
                    radioactive: true,
                    weight: 186.95575,
                    percent: 62.6,
                },
                {
                    number: 185,
                    radioactive: false,
                    weight: 184.952955,
                    percent: 37.4,
                },
            ],
        },
    },
    {
        number: 76,
        color: "yellow",
        name: "osmium",
        title: {
            en: "Osmium",
            de: "Osmium",
            fr: "Osmium",
        },
        letter: "Os",
        weight: "190.23(3)",
        firstIonization: 8.438,
        electronegativity: "2.2",
        info: {
            image: "images/osmium.jpg",
            link: "pdf-elements/osmium.pdf",
            imageText:
                "The <i>n</i>(<sup>187</sup>Os)/<i>n</i>(<sup>186</sup>Os) isotope ratios in rocks can be transferred to fluids, such as magmas.",
            description: "",
            isotopes: [
                {
                    number: 192,
                    radioactive: false,
                    weight: 191.96148,
                    percent: 40.78,
                },
                {
                    number: 190,
                    radioactive: false,
                    weight: 189.95844,
                    percent: 26.26,
                },
                {
                    number: 189,
                    radioactive: false,
                    weight: 188.95814,
                    percent: 16.15,
                },
                {
                    number: 188,
                    radioactive: false,
                    weight: 187.95584,
                    percent: 13.24,
                },
                {
                    number: 187,
                    radioactive: false,
                    weight: 186.95575,
                    percent: 1.96,
                },
                {
                    number: 186,
                    radioactive: true,
                    weight: 185.95384,
                    percent: 1.59,
                },
                {
                    number: 184,
                    radioactive: true,
                    weight: 184.952489,
                    percent: 0.02,
                },
            ],
        },
    },
    {
        number: 77,
        color: "yellow",
        name: "iridium",
        title: {
            en: "Iridium",
            de: "Iridium",
            fr: "Iridium",
        },
        letter: "Ir",
        weight: "192.217(2)",
        firstIonization: 8.967,
        electronegativity: "2.2",
        info: {
            image: "images/iridium.jpg",
            link: "pdf-elements/iridium.pdf",
            imageText:
                "This is a radiograph of a weld using <sup>192</sup>Ir as the source of radiation. The gamma rays given off by the isotope allows for imperfections in the weld to be seen on the radiograph.",
            description: "",
            isotopes: [
                {
                    number: 193,
                    radioactive: false,
                    weight: 192.96292,
                    percent: 62.7,
                },
                {
                    number: 191,
                    radioactive: false,
                    weight: 190.96059,
                    percent: 37.3,
                },
            ],
        },
    },
    {
        number: 78,
        color: "yellow",
        name: "platinum",
        title: {
            en: "Platinum",
            de: "Platin",
            fr: "Platine",
        },
        letter: "Pt",
        weight: "195.084(9)",
        firstIonization: 8.958,
        electronegativity: "2.2",
        info: {
            image: "images/platinum.jpg",
            link: "pdf-elements/platinum.pdf",
            imageText:
                "The decay of <sup>190</sup>Pt can be used for dating iron meteorites.",
            description: "",
            isotopes: [
                {
                    number: 195,
                    radioactive: false,
                    weight: 194.964792,
                    percent: 33.775,
                },
                {
                    number: 194,
                    radioactive: false,
                    weight: 193.962681,
                    percent: 32.864,
                },
                {
                    number: 196,
                    radioactive: false,
                    weight: 195.964952,
                    percent: 25.211,
                },
                {
                    number: 198,
                    radioactive: false,
                    weight: 197.96789,
                    percent: 7.356,
                },
                {
                    number: 192,
                    radioactive: false,
                    weight: 191.96104,
                    percent: 0.782,
                },
                {
                    number: 190,
                    radioactive: true,
                    weight: 189.95993,
                    percent: 0.012,
                },
            ],
        },
    },
    {
        number: 79,
        color: "blue",
        name: "gold",
        title: {
            en: "Gold",
            de: "Gold",
            fr: "Or",
        },
        letter: "Au",
        weight: "196.966570(4)",
        firstIonization: 9.225,
        electronegativity: "2.4",
        info: {
            image: "images/gold.jpg",
            link: "pdf-elements/gold.pdf",
            imageText:
                "<sup>195</sup>Au has been used to study particle movement within the lungs of rats.",
            description: "",
            isotopes: [
                {
                    number: 197,
                    radioactive: false,
                    weight: 196.966569,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 80,
        color: "yellow",
        name: "mercury",
        title: {
            en: "Mercury",
            de: "Quecksilber",
            fr: "Mercure",
        },
        letter: "Hg",
        weight: "200.592(3)",
        firstIonization: 10.43,
        electronegativity: "1.9",
        info: {
            image: "images/mercury.jpg",
            link: "pdf-elements/mercury.pdf",
            imageText:
                "<sup>202</sup>Hg was added to small watersheds to study the fate of mercury from atmospheric deposition in pristine lakes as part of the METAALICUS study.",
            description: "",
            isotopes: [
                {
                    number: 202,
                    radioactive: false,
                    weight: 201.970643,
                    percent: 29.74,
                },
                {
                    number: 200,
                    radioactive: false,
                    weight: 199.968327,
                    percent: 23.14,
                },
                {
                    number: 199,
                    radioactive: false,
                    weight: 198.968281,
                    percent: 16.94,
                },
                {
                    number: 201,
                    radioactive: false,
                    weight: 200.970303,
                    percent: 13.17,
                },
                {
                    number: 198,
                    radioactive: false,
                    weight: 197.966769,
                    percent: 10.04,
                },
                {
                    number: 204,
                    radioactive: false,
                    weight: 203.973494,
                    percent: 6.82,
                },
                {
                    number: 196,
                    radioactive: false,
                    weight: 195.96583,
                    percent: 0.15,
                },
            ],
        },
    },
    {
        number: 81,
        color: "pink",
        name: "thallium",
        title: {
            en: "Thallium",
            de: "Thallium",
            fr: "Thallium",
        },
        letter: "Tl",
        conventionalWeight: 204.38,
        weight: "[204.382, 204.385]",
        firstIonization: 6.108,
        electronegativity: "1.8",
        info: {
            image: "images/thallium.jpg",
            link: "pdf-elements/thallium.pdf",
            imageText:
                "Thallium radionuclides can be injected into a patient intravenously during a myocardium perfusion test. Areas of decreased blood flow or damaged tissue will be illuminated by the radionuclide.",
            description: "",
            isotopes: [
                {
                    number: 205,
                    radioactive: false,
                    weight: 204.974428,
                    percent: 70.48,
                    moleFraction: [
                        {
                            fractionLower: 0.7041,
                            fractionUpper: 0.7056,
                        },
                    ],
                },
                {
                    number: 203,
                    radioactive: false,
                    weight: 202.972345,
                    percent: 29.52,
                    moleFraction: [
                        {
                            fractionLower: 0.2944,
                            fractionUpper: 0.2959,
                        },
                    ],
                },
            ],
        },
    },
    {
        number: 82,
        color: "yellow",
        name: "lead",
        title: {
            en: "Lead",
            de: "Blei",
            fr: "Plomb",
        },
        letter: "Pb",
        weight: "207.2(1)",
        firstIonization: 7.416,
        electronegativity: "1.9",
        info: {
            image: "images/lead.jpg",
            link: "pdf-elements/lead.pdf",
            imageText:
                "Airborne particles from China have relatively higher amounts of <sup>208</sup>Pb, compared to airborne particles from Asia and North America. This knowledge could have implications in understanding the mixing of particles in the atmosphere and how pollutants are transported over vast distances.",
            description: "",
            isotopes: [
                {
                    number: 208,
                    radioactive: false,
                    weight: 207.976653,
                    percent: 52.4,
                },
                {
                    number: 206,
                    radioactive: false,
                    weight: 205.974466,
                    percent: 24.1,
                },
                {
                    number: 207,
                    radioactive: false,
                    weight: 206.975897,
                    percent: 22.1,
                },
                {
                    number: 204,
                    radioactive: false,
                    weight: 203.973044,
                    percent: 1.4,
                },
            ],
        },
    },
    {
        number: 83,
        color: "blue",
        name: "bismuth",
        title: {
            en: "Bismuth",
            de: "Bismut",
            fr: "Bismuth",
        },
        letter: "Bi",
        weight: "208.98040(1)",
        firstIonization: 7.285,
        electronegativity: "1.9",
        info: {
            image: "images/bismuth.jpg",
            link: "pdf-elements/bismuth.pdf",
            imageText:
                "Melanoma on a patient’s foot. <sup>212</sup>Bi and <sup>213</sup>Bi are both used as bismuth-labeled monoclonal antibodies to treat cancer cells from melanoma.",
            description: "",
            isotopes: [
                {
                    number: 209,
                    radioactive: true,
                    weight: 208.9804,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 84,
        color: "white",
        name: "polonium",
        title: {
            en: "Polonium",
            de: "Polonium",
            fr: "Polonium",
        },
        letter: "Po",
        weight: "",
        firstIonization: 8.416,
        electronegativity: "2.0",
        noStableIsotope: true,
        info: {
            image: "images/polonium.jpg",
            link: "pdf-elements/polonium.pdf",
            imageText:
                "Staticmaster<sup>TM</sup> Alpha Ionizing Brushes for cleaning optical surfaces and photographic films have a soft, non-abrasive brush and a <sup>210</sup>Po cartridge.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 85,
        color: "white",
        name: "astatine",
        title: {
            en: "Astatine",
            de: "Astat",
            fr: "Astate",
        },
        letter: "At",
        weight: "",
        firstIonization: 9.597,
        electronegativity: "2.2",
        noStableIsotope: true,
        info: {
            image: "images/astatine.jpg",
            link: "pdf-elements/astatine.pdf",
            imageText:
                "<sup>211</sup>At treats hyperthyroidism and thyroid cancer.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 86,
        color: "white",
        name: "radon",
        title: {
            en: "Radon",
            de: "Radon",
            fr: "Radon",
        },
        letter: "Rn",
        weight: "",
        firstIonization: 10.74,
        noStableIsotope: true,
        info: {
            image: "images/radon.jpg",
            link: "pdf-elements/radon.pdf",
            imageText:
                "Air-water equilibrator, which strips radon out of water and into the gas phase so it can be used as a groundwater tracer.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 87,
        color: "white",
        name: "francium",
        title: {
            en: "Francium",
            de: "Francium",
            fr: "Francium",
        },
        letter: "Fr",
        weight: "",
        firstIonization: 4.072,
        electronegativity: "0.7",
        noStableIsotope: true,
        info: {
            image: "images/francium.jpg",
            link: "pdf-elements/francium.pdf",
            imageText:
                "Francium was discovered in 1939 by Marguerite Perey, a physicist at the Radium Institute in Paris, France.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 88,
        color: "white",
        name: "radium",
        title: {
            en: "Radium",
            de: "Radium",
            fr: "Radium",
        },
        letter: "Ra",
        weight: "",
        firstIonization: 5.278,
        electronegativity: "0.9",
        noStableIsotope: true,
        info: {
            image: "images/radium.jpg",
            link: "pdf-elements/radium.pdf",
            imageText:
                "Brachytherapy seeds shown with a penny for scale. <sup>226</sup>Ra is used in brachytherapy, which is a method of localized treatment of various types of cancer.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 89,
        color: "white",
        name: "actinium",
        title: {
            en: "Actinium",
            de: "Actinium",
            fr: "Actinium",
        },
        letter: "Ac",
        weight: "",
        firstIonization: 5.17,
        electronegativity: "1.1",
        noStableIsotope: true,
        info: {
            image: "images/actinium.jpg",
            link: "pdf-elements/actinium.pdf",
            imageText:
                "The Medical Actinium for Therapeutic Treatment (MATT) is a separations process that recovers <sup>225</sup>Ac from unused nuclear fuel so the isotope can be used in cancer treatment and research.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 90,
        color: "yellow",
        name: "thorium",
        title: {
            en: "Thorium",
            de: "Thorium",
            fr: "Thorium",
        },
        letter: "Th",
        weight: "232.0377(4)",
        firstIonization: 6.306,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/thorium.jpg",
            link: "pdf-elements/thorium.pdf",
            imageText:
                "<sup>234</sup>Th can be used for tracing and estimating the flux of organic carbon in the ocean.",
            description: "",
            isotopes: [
                {
                    number: 232,
                    radioactive: true,
                    weight: 232.03806,
                    percent: 99.98,
                },
                {
                    number: 230,
                    radioactive: true,
                    weight: 230.03313,
                    percent: 0.02,
                },
            ],
        },
    },
    {
        number: 91,
        color: "blue",
        name: "protactinium",
        title: {
            en: "Protactinium",
            de: "Protactinium",
            fr: "Protactinium",
        },
        letter: "Pa",
        weight: "231.03588(1)",
        firstIonization: 5.89,
        electronegativity: "1.5",
        noStableIsotope: true,
        info: {
            image: "images/protactinium.jpg",
            link: "pdf-elements/protactinium.pdf",
            imageText:
                "Diagram of <sup>231</sup>Pa -<sup> 230</sup>Th fractionation in the oceans. NADW stands for North Atlantic Deep Water.",
            description: "",
            isotopes: [
                {
                    number: 231,
                    radioactive: true,
                    weight: 231.03588,
                    percent: 100,
                },
            ],
        },
    },
    {
        number: 92,
        color: "yellow",
        name: "uranium",
        title: {
            en: "Uranium",
            de: "Uran",
            fr: "Uranium",
        },
        letter: "U",
        weight: "238.02891(3)",
        firstIonization: 6.194,
        electronegativity: "1.7",
        noStableIsotope: true,
        info: {
            image: "images/uranium.jpg",
            link: "pdf-elements/uranium.pdf",
            imageText:
                "Boiling water reactor fuel can be <sup>235</sup>U enriched as uranium dioxide.",
            description: "",
            isotopes: [
                {
                    number: 238,
                    radioactive: true,
                    weight: 238.05079,
                    percent: 99.2742,
                },
                {
                    number: 235,
                    radioactive: true,
                    weight: 235.04393,
                    percent: 0.7204,
                },
                {
                    number: 234,
                    radioactive: true,
                    weight: 234.04095,
                    percent: 0.0054,
                },
            ],
        },
    },
    {
        number: 93,
        color: "white",
        name: "neptunium",
        title: {
            en: "Neptunium",
            de: "Neptunium",
            fr: "Neptunium",
        },
        letter: "Np",
        weight: "",
        firstIonization: 6.265,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/neptunium.jpg",
            link: "pdf-elements/neptunium.pdf",
            imageText:
                "<sup>237</sup>Np is fissionable and can be used in fast neutron reactors or in nuclear weapons.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 94,
        color: "white",
        name: "plutonium",
        title: {
            en: "Plutonium",
            de: "Plutonium",
            fr: "Plutonium",
        },
        letter: "Pu",
        weight: "",
        firstIonization: 6.026,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/plutonium.jpg",
            link: "pdf-elements/plutonium.pdf",
            imageText:
                "<sup>238</sup>Pu is used in radiothermal generators as a heat source to produce electricity to power unmanned spacecrafts, such as the Cassini Orbiter.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 95,
        color: "white",
        name: "americium",
        title: {
            en: "Americium",
            de: "Americium",
            fr: "Américium",
        },
        letter: "Am",
        weight: "",
        firstIonization: 5.973,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/americium.jpg",
            link: "pdf-elements/americium.pdf",
            imageText:
                "<sup>241</sup>Am is used in smoke detectors as an ionization source to detect smoke.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 96,
        color: "white",
        name: "curium",
        title: {
            en: "Curium",
            de: "Curium",
            fr: "Curium",
        },
        letter: "Cm",
        weight: "",
        firstIonization: 6.02,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/curium.jpg",
            link: "pdf-elements/curium.pdf",
            imageText:
                "This is a schematic drawing of the inside of a reactor core at the Oak Ridge National Laboratory’s High Flux Isotope Reactor facility. <sup>244</sup>Cm is used as the target in the flux trap.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 97,
        color: "white",
        name: "berkelium",
        title: {
            en: "Berkelium",
            de: "Berkelium",
            fr: "Berkélium",
        },
        letter: "Bk",
        weight: "",
        firstIonization: 6.23,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/berkelium.jpg",
            link: "pdf-elements/berkelium.pdf",
            imageText:
                "The green fluid in the tip of the vial is <sup>249</sup>Bk, which was a critical isotope in the synthesis of Ununseptium (Uus). ",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 98,
        color: "white",
        name: "californium",
        title: {
            en: "Californium",
            de: "Californium",
            fr: "Californium",
        },
        letter: "Cf",
        weight: "",
        firstIonization: 6.3,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/californium.jpg",
            link: "pdf-elements/californium.pdf",
            imageText:
                "<sup>252</sup>Cf is a logging tool used in well logging to identify downhole properties of the well bore.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 99,
        color: "white",
        name: "einsteinium",
        title: {
            en: "Einsteinium",
            de: "Einsteinium",
            fr: "Einsteinium",
        },
        letter: "Es",
        weight: "",
        firstIonization: 6.42,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/einsteinium.jpg",
            link: "pdf-elements/einsteinium.pdf",
            imageText:
                "Albert Einstein, from whom einsteinium derives its name.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 100,
        color: "white",
        name: "fermium",
        title: {
            en: "Fermium",
            de: "Fermium",
            fr: "Fermium",
        },
        letter: "Fm",
        weight: "",
        firstIonization: 6.5,
        electronegativity: "1.3",
        noStableIsotope: true,
        info: {
            image: "images/fermium.jpg",
            link: "pdf-elements/fermium.pdf",
            imageText:
                "The first successful hydrogen bomb test (Ivy-Mike) in 1952 produced <sup>255</sup>Fm, which was the first fermium isotope ever detected.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 101,
        color: "white",
        name: "mendelevium",
        firstIonization: 6.58,
        electronegativity: "1.3",
        title: {
            en: "Mendelevium",
            de: "Mendelevium",
            fr: "Mendélévium",
        },
        letter: "Md",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/mendelevium.jpg",
            link: "pdf-elements/mendelevium.pdf",
            imageText:
                "Mendelevium is named after Dmitri Mendeleev to honor his contributions to the development of the periodic table of the elements.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 102,
        color: "white",
        name: "nobelium",
        firstIonization: 6.65,
        electronegativity: "1.5",
        title: {
            en: "Nobelium",
            de: "Nobelium",
            fr: "Nobélium",
        },
        letter: "No",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/nobelium.jpg",
            link: "pdf-elements/nobelium.pdf",
            imageText:
                "This element was originally named for Alfred Nobel, the discoverer of dynamite and founder of the Nobel prizes.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 103,
        color: "white",
        name: "lawrencium",
        title: {
            en: "Lawrencium",
            de: "Lawrencium",
            fr: "Lawrencium",
        },
        letter: "Lr",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/lawrencium.jpg",
            link: "pdf-elements/lawrencium.pdf",
            imageText:
                "This is a photograph of Ernst O. Lawrence, who invented the cyclotron. Element 103 is named after him.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 104,
        color: "white",
        name: "rutherfordium",
        title: {
            en: "Rutherfordium",
            de: "Rutherfordium",
            fr: "Rutherfordium",
        },
        letter: "Rf",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/rutherfordium.jpg",
            link: "pdf-elements/rutherfordium.pdf",
            imageText:
                "A series of heavy isotopes produced by successive alpha decays beginning with copernicium and including <sup>261</sup>Rf provided evidence for successful synthesis of copernicium.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 105,
        color: "white",
        name: "dubnium",
        title: {
            en: "Dubnium",
            de: "Dubnium",
            fr: "Dubnium",
        },
        letter: "Db",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/dubnium.jpg",
            link: "pdf-elements/dubnium.pdf",
            imageText:
                "The cyclotron U-300 is a 300-cm heavy-ion cyclotron built at the international Joint Institute for Nuclear Research (JINR) and put into operation in 1960.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 106,
        color: "white",
        name: "seaborgium",
        title: {
            en: "Seaborgium",
            de: "Seaborgium",
            fr: "Seaborgium",
        },
        letter: "Sg",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/seaborgium.jpg",
            link: "pdf-elements/seaborgium.pdf",
            imageText:
                "Seaborgium is named after Glenn T. Seaborg who synthesized a number of transuranium elements.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 107,
        color: "white",
        name: "bohrium",
        title: {
            en: "Bohrium",
            de: "Bohrium",
            fr: "Bohrium",
        },
        letter: "Bh",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/bohrium.jpg",
            link: "pdf-elements/bohrium.pdf",
            imageText:
                "Ernest Lawrence (left) & Neils Bohr (right) are pictured above. The element is named for Nils Bohr, the Nobel Prize winning physicist. Neils Bohr’s work on the structure of atoms won him the Nobel Prize in Physics in 1922.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 108,
        color: "white",
        name: "hassium",
        title: {
            en: "Hassium",
            de: "Hassium",
            fr: "Hassium",
        },
        letter: "Hs",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/hassium.jpg",
            link: "pdf-elements/hassium.pdf",
            imageText:
                "Hassium was first synthesized by German scientists at the GSI Center for Heavy Ion Research in Darmstadt, Germany in 1984 using the nuclear reaction <sup>208</sup>Pb (<sup>58</sup>Fe, n) <sup>265</sup>Hs. The element is named for Hassia whose former capital was Darmstadt.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 109,
        color: "white",
        name: "meitnerium",
        title: {
            en: "Meitnerium",
            de: "Meitnerium",
            fr: "Meitnerium",
        },
        letter: "Mt",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/meitnerium.jpg",
            link: "pdf-elements/meitnerium.pdf",
            imageText:
                "Fritz Strassmann (left), Lise Meitner (center), Otto Hahn (right) are pictured above. Lise Meitner is a nuclear physicist, after whom meitnerium is named.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 110,
        color: "white",
        name: "darmstadtium",
        title: {
            en: "Darmstadtium",
            de: "Darmstadtium",
            fr: "Darmstadtium",
        },
        letter: "Ds",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/darmstadtium.jpg",
            link: "pdf-elements/darmstadtium.pdf",
            imageText:
                "Darmstadtium was first synthesized by an international team of scientists at the GSI Helmholtz Center for Heavy Ion Research in Darmstadt, Germany in 1994 using the nuclear reaction <sup>208</sup>Pb (<sup>62</sup>Ni, n) <sup>269</sup>Ds. ",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 111,
        color: "white",
        name: "roentgenium",
        title: {
            en: "Roentgenium",
            de: "Roentgenium",
            fr: "Roentgenium",
        },
        letter: "Rg",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/roentgenium.jpg",
            link: "pdf-elements/roentgenium.pdf",
            imageText:
                "Wilhelm Conrad Roentgen, discoverer of X-rays, after whom roentgenium was named.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 112,
        color: "white",
        name: "copernicium",
        title: {
            en: "Copernicium",
            de: "Copernicium",
            fr: "Copernicium",
        },
        letter: "Cn",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/copernicium.jpg",
            link: "pdf-elements/copernicium.pdf",
            imageText:
                "View inside of the linear accelerator used to create copernicium at GSI.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 113,
        color: "white",
        name: "nihonium",
        title: {
            en: "Nihonium",
            de: "Nihonium",
            fr: "Nihonium",
        },
        letter: "Nh",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/ununtrium.jpg",
            link: "pdf-elements/nihonium.pdf",
            imageText:
                "Photograph of the cyclotron that was used to synthesize Moscovium and Nihonium.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 114,
        color: "white",
        name: "flerovium",
        title: {
            en: "Flerovium",
            de: "Flerovium",
            fr: "Flérovium",
        },
        letter: "Fl",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/flerovium.jpg",
            link: "pdf-elements/flerovium.pdf",
            imageText:
                "In 1999, a collaboration of scientists work together to synthesis Flerovium. They used nuclear reaction experiments to eventually produce <sup>287</sup>Fl, by cross-bombardments of <sup>48</sup>Ca with both (even-A) <sup>242</sup>Pu and (odd A) <sup>245</sup>Cm.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 115,
        color: "white",
        name: "moscovium",
        title: {
            en: "Moscovium",
            de: "Moscovium",
            fr: "Moscovium",
        },
        letter: "Mc",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/ununpentium.jpg",
            link: "pdf-elements/moscovium.pdf",
            imageText:
                "Accelerated <sup>48</sup>Ca ion colliding with a <sup>243</sup>Am atom in a cyclotron creating moscovium and nihonium.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 116,
        color: "white",
        name: "livermorium",
        title: {
            en: "Livermorium",
            de: "Livermorium",
            fr: "Livermorium",
        },
        letter: "Lv",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/livermorium.jpg",
            link: "pdf-elements/livermorium.pdf",
            imageText:
                "Livermorium was synthesized in the heavy ion cyclotron U-400 at JINR.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 117,
        color: "white",
        name: "tennessine",
        title: {
            en: "Tennessine",
            de: "Tennessin",
            fr: "Tennessine",
        },
        letter: "Ts",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/ununseptium.jpg",
            link: "pdf-elements/tennessine.pdf",
            imageText: "Tennessine decay chain.",
            description: "",
            isotopes: [],
        },
    },
    {
        number: 118,
        color: "white",
        name: "oganesson",
        title: {
            en: "Oganesson",
            de: "Oganesson",
            fr: "Oganesson",
        },
        letter: "Og",
        weight: "",
        noStableIsotope: true,
        info: {
            image: "images/ununoctium.jpg",
            link: "pdf-elements/oganesson.pdf",
            imageText:
                "Artistic interpretation of the production of oganesson as it travels to the detector through the accelerator.",
            description: "",
            isotopes: [],
        },
    },
];
